// Section 2. Your embroidery.
//
// Composes: the placement picker (with conflict-locked tiles), content-type
// toggle, upload or text+font editor, size slider. Used for both new spots
// (when the customer hits "+ Add another spot") and re-edits (when they
// hit Edit on a saved-spot tile). Pricing + cart-payload logic lives in
// `../pricing.ts` so this file stays UI-only.

import * as React from "react";
import { dateIsoToRoman } from "../util/romanNumerals";
import { easedScrollTo } from "../util/scroll";
import { stageLockedSizeMm, isBestDadSockTemplate } from "../stages";
import { flowCopy } from "../copy";
import { designUpsellFor, flowKindFor, photoUpgradeUpsellFor } from "../flow";
import { isPhotoLike } from "../util/photoDetect";
import { isShrinkableImage, shrinkImage } from "../util/shrinkImage";
import {
  effectiveUpsellPriceCents,
  effectiveUpsellStyleSurchargeCents,
  MATCH_DESIGN_SURCHARGE_CENTS,
} from "../pricing";
import type { Config, Placement } from "../types";
import { PlacementCard } from "../components/PlacementCard";
import { FontTile } from "../components/FontTile";
import { WordArtStyleTile } from "../components/WordArtStyleTile";
import { WORD_ART_PRESETS, WORD_ART_TEMPLATE_TAG } from "../wordart/presets";
import type { WordArtPreset } from "../wordart/presets";
import { SizeSlider } from "../components/SizeSlider";
import { LogoReuseTile } from "../components/LogoReuseTile";
import { ThreadPicker, MATCH_DESIGN_CODE } from "../components/ThreadPicker";
import { uploadStaging, uploadedFileUrl } from "../api";
import { isSleeveSlug, isSleeveEmojiKind } from "../util/sleeve";
import { emitAnalytics } from "../analytics";
import { textChangePatch, textOnlyPatch } from "../textDraft";
import type { PriorLogo } from "../pricing";

export type ContentMode = "logo" | "text" | "both";
export type Arrangement = "above" | "below" | "left" | "right";

export type EmbroideryDraft = {
  placementSlug: string | null;
  mode: ContentMode | null;
  text: string;
  fontId: string | null;
  /** Word Art (2026-07): id of the chosen Word Art preset. Mutually
   *  exclusive with fontId - the style IS the face. Null on non-Word-Art
   *  spots. */
  wordArtStyleId: string | null;
  uploadStagingKey: string | null;
  uploadFilename: string | null;
  uploadMimeType: string | null;
  /** sha256-hex of the uploaded file's raw bytes. Used by Phase 19 cross-
   *  order digitisation memory: emitted as a cart property + matched on
   *  future orders to skip the fee. */
  uploadFileHash: string | null;
  /** True when the worker recognised this file as already-digitised by
   *  this customer in a past order. Set by /api/upload. */
  uploadAlreadyDigitised: boolean;
  /**
   * Upload-later (2026-07-17): true when the customer chose "I'll send
   * it later" instead of uploading artwork now. Satisfies the upload
   * (and logo-thread) requirements so the spot can be saved and bought;
   * the order ships with an upload-pending hold until the artwork
   * arrives via the post-purchase portal (/upload on the worker).
   * Mutually exclusive with uploadStagingKey - a real upload clears it.
   */
  uploadLater?: boolean;
  /** Where the text sits relative to the logo when mode === "both". */
  arrangement: Arrangement | null;
  sizeMm: number;
  /**
   * Isacord thread codes for the TEXT portion of the spot. Capped at 1 by
   * the picker (text is always a single colour). Empty when mode === "logo".
   */
  textThreadCodes: string[];
  /**
   * Isacord thread codes for the LOGO portion of the spot. Capped at
   * MAX_LOGO_THREADS_PER_SPOT (3) by the picker so digitisation stays clean.
   * Empty when mode === "text".
   */
  logoThreadCodes: string[];
  /**
   * Phase 17c: id of an add-on (Upsell) the customer picked AS THE
   * CONTENT MODE for this spot. Currently scoped to upsells with
   *   appliesTo === "logo" && replacesStep === "logo-upload"
   * (e.g. "Pet portrait digitization", "Kid drawing scan"). When set,
   * `mode` is locked to "logo" and the upload step's label swaps to the
   * upsell's swapLabel. The upsell's priceCents stacks into the spot's
   * total. Null = no replacement add-on; standard Logo / Text / Both flow.
   */
  addonUpsellId: string | null;
  /**
   * Phase 33b: how many pets the customer's tracking (or how many subjects
   * for any countMax-enabled upsell). Defaults to 1. Each unit beyond 1
   * bills the upsell's perCountCents. Null when the active addon doesn't
   * support a count stepper.
   */
  addonCount: number | null;
  /**
   * Phase 33c: true when the customer ticked "coloured + filled" on a
   * pet-portrait / kid-drawing style toggle. Bills the upsell's
   * styleSurchargeCents. Default false (line drawing, free).
   */
  addonStyleColoured: boolean;
  /**
   * 2026-07-19: person flow only. RK's people line drawings are faceless
   * by default (the classic look). When true the customer asked for
   * simple stitched facial features, accepting the warning that faces
   * can crowd and lose likeness with several people or on small
   * placements. Free either way - a style preference, not an upsell.
   */
  includeFaces: boolean;
  /**
   * 2026-07-19: person flow only. Stylised single-line drawing - the
   * artist draws the whole subject in ONE continuous stroke. Free (a
   * style preference like faces, not an upsell). Mutually exclusive
   * with addonStyleColoured; both false = standard line drawing.
   */
  addonStyleSingleLine: boolean;
  /**
   * Phase 33d: extra (stackable) upsell ids piggybacking on this spot —
   * the Heart / Paw emoji decorations from the sleeve cross-sell. Each
   * id's priceCents stacks into the spot total + appears as its own
   * cart line property so the embroidery team can stitch the glyph.
   */
  extraAddonIds: string[];
  /**
   * Phase 39c: additional uploads beyond the primary one. Pet flow only
   * — the customer can upload up to (countMax - 1) extra pet photos and
   * we'll combine them into one stitched design. Each extra upload
   * stacks the upsell's perCountCents into the spot total. On non-pet
   * flows this stays empty.
   */
  extraUploads: Array<{
    stagingKey: string;
    filename: string;
    mimeType: string | null;
    fileHash: string | null;
  }>;
  /**
   * Phase 29: URL of the customer's original logo file from a legacy
   * Easify/Tigren order. When set, the upload step shows a "Your old
   * file from <order>" hint with a download link so the customer can
   * grab their original artwork (often a .docx with the logo inside)
   * and re-upload it as PNG/JPG/SVG/PDF. Not emitted in cart props on
   * checkout — once the customer re-uploads we use the fresh staging
   * key, which goes through the same digitisation-memory check.
   */
  legacyUploadUrl: string | null;
  /**
   * Preview-only: when true the visual preview runs in-browser AI
   * background removal on the uploaded artwork so it reads clearly on
   * the garment colour. Does NOT affect the file the embroidery team
   * receives, the cart, or pricing — purely a visualiser aid.
   */
  removeBg?: boolean;
  /**
   * Varsity / college style only: the Isacord thread code for the
   * stitched outline around the arched text. Null until the customer
   * picks one. Emitted as a cart property so the embroidery team knows
   * the outline colour.
   */
  outlineThreadCode: string | null;
  /**
   * Phase 67i: name of the past order this design came from when the
   * customer reused it. Set by `pickProof` (verified PDF reuse) and the
   * past-order spot lookup decoder; preserved through `pickReuse` (cart
   * reuse) so re-using a cart line that was itself reused still carries
   * the original order. Emitted on the new order as a visible "Spot N
   * reused from" property so production can see the provenance without
   * digging.
   */
  sourceOrderName: string | null;
  /**
   * 2026-08-21: same-session reuse. The 1-based spot number this design
   * was picked from via the reuse tile, so the order shows "reused from
   * Spot N of this order" instead of claiming a past order. Mutually
   * informative with sourceOrderName (cross-order provenance wins when
   * both are set). Optional so persisted drafts stay valid.
   */
  reusedFromSpot?: number | null;
  /**
   * Pet-photo trap (2026-07-12): true when util/photoDetect classified
   * this spot's upload as photo-like (logo flow with a configured
   * photoUpgrade only). Drives the in-place "Upgrade to Portrait" card,
   * the honest "Match the design" hint clause, and the saved-spot
   * "stitched as a simplified logo" summary note. Optional so older
   * persisted/reuse-decoded drafts stay valid (absent = false).
   */
  uploadPhotoLike?: boolean;
  /**
   * Pet-photo trap: true once the customer tapped "Keep as a simple
   * logo" on this spot - the upgrade card never re-nags this upload.
   */
  photoUpgradeDeclined?: boolean;
  /**
   * W4 phase 2 (on-garment preview): where the customer dragged the
   * design inside the calibrated photo zone, in mm relative to the ZONE
   * CENTRE (x grows right, y grows down). Null/absent = centred.
   * PREVIEW + PRODUCTION HINT ONLY - no pricing semantics; sizeMm stays
   * the price/production authority. Emitted at add-to-cart as
   * `_spot_N_preview_offset` when non-zero (pricing.propertiesFor).
   * Optional so older persisted/reuse-decoded drafts stay valid.
   */
  previewOffset?: { xMm: number; yMm: number } | null;
};

export const MAX_LOGO_THREADS_PER_SPOT = 3;

// Wave 1: page-level interaction latch. The editor can now mount on page
// load (App auto-opens it, killing the "Start designing" tap), and on
// single-placement templates the auto-pick advances the accordion within
// the first frames. The follow-the-flow scroll must never run before the
// customer has touched the page, or the PDP would scroll itself down to
// the customiser on load. Any pointerdown/keydown flips the latch and the
// eased scrolls behave exactly as before.
let pageHasInteracted = false;
if (typeof document !== "undefined") {
  const markInteracted = () => {
    pageHasInteracted = true;
  };
  document.addEventListener("pointerdown", markInteracted, {
    capture: true,
    once: true,
  });
  document.addEventListener("keydown", markInteracted, {
    capture: true,
    once: true,
  });
}

export function emptyDraft(): EmbroideryDraft {
  return {
    placementSlug: null,
    mode: null,
    text: "",
    fontId: null,
    wordArtStyleId: null,
    uploadStagingKey: null,
    uploadFilename: null,
    uploadMimeType: null,
    uploadFileHash: null,
    uploadAlreadyDigitised: false,
    uploadLater: false,
    arrangement: null,
    sizeMm: 80,
    textThreadCodes: [],
    logoThreadCodes: [],
    addonUpsellId: null,
    addonCount: null,
    addonStyleColoured: false,
    includeFaces: false,
    addonStyleSingleLine: false,
    extraAddonIds: [],
    extraUploads: [],
    legacyUploadUrl: null,
    removeBg: false,
    outlineThreadCode: null,
    sourceOrderName: null,
    uploadPhotoLike: false,
    photoUpgradeDeclined: false,
    previewOffset: null,
  };
}

// True when the draft has enough information to be saved as a real spot.
// Thread colours are mandatory: text picks 1 thread, logo picks at least 1
// thread, both picks at least one of each.
//
// Phase 67d: when the template has textRequired=true, the second arg
// passes the config so we can fail the gate until a non-empty name is
// typed even on "logo"-shaped drafts that haven't promoted to "both" yet.
// Phase 67h: the active placement may opt out of the template locks
// (bypassTemplateLocks), in which case textRequired is effectively
// false for that spot.
export function isDraftComplete(
  draft: EmbroideryDraft,
  config?: {
    product?: { textRequired?: boolean; lockedFontId?: string | null };
    placements?: Array<{ slug: string; bypassTemplateLocks?: boolean }>;
  },
): boolean {
  if (!draft.placementSlug || !draft.mode) return false;
  // 2026-07-25: the slug must exist ON THIS TEMPLATE, not merely be
  // non-empty. A design reused from another garment could carry a foreign
  // slug, which rendered no editor steps at all yet still satisfied this
  // gate - the spot saved, and then priced at $0 because the pricing loop
  // skips placements it cannot resolve. Only enforced when the caller
  // actually passed a placement list.
  if (
    config?.placements &&
    config.placements.length > 0 &&
    !config.placements.some((p) => p.slug === draft.placementSlug)
  ) {
    return false;
  }
  const bypass =
    !!config?.placements?.find((p) => p.slug === draft.placementSlug)
      ?.bypassTemplateLocks;
  const textRequired = !bypass && !!config?.product?.textRequired;
  if (textRequired && !draft.text.trim()) return false;
  // Upload-later: "I'll send it later" stands in for the artwork, and
  // (since threads are usually picked to match artwork we haven't seen)
  // it relaxes the logo-thread requirement too - production matches the
  // artwork when it lands.
  const hasArtwork = !!draft.uploadStagingKey || !!draft.uploadLater;
  const hasLogoThreads =
    draft.logoThreadCodes.length > 0 || !!draft.uploadLater;
  if (draft.mode === "logo") {
    return hasArtwork && hasLogoThreads;
  }
  // Word Art (2026-07): the chosen preset IS the face, so a
  // wordArtStyleId satisfies the font requirement. Only the Word Art
  // gallery can set it, so accepting either is safe on every flow.
  //
  // 2026-07-22: a template-locked font ALSO satisfies it, matching
  // missingReasons() which has always counted it. The two disagreed: a
  // locked-font template suppresses the font picker, so if nothing seeded
  // draft.fontId this returned false while missingReasons found nothing to
  // report. The customer got an EMPTY "Still to do" list above a dead
  // "Save spot" with no way to discover what was wrong. Hit the Father's Day
  // bundle's cap and socks spots, both of which lock College.
  const hasFace =
    !!draft.fontId ||
    !!draft.wordArtStyleId ||
    (!bypass && !!config?.product?.lockedFontId);
  if (draft.mode === "text") {
    return !!draft.text.trim() && hasFace && draft.textThreadCodes.length > 0;
  }
  if (draft.mode === "both") {
    return !!(
      hasArtwork &&
      draft.text.trim() &&
      hasFace &&
      draft.arrangement &&
      draft.textThreadCodes.length > 0 &&
      hasLogoThreads
    );
  }
  return false;
}

// Phase B2: precise pending-state messages. Returns a list of the
// specific things still missing before the spot can be saved, so the
// editor can say "No font chosen" instead of a vague "finish the spot".
export function missingReasons(
  draft: EmbroideryDraft,
  config?: {
    product?: { textRequired?: boolean; lockedFontId?: string | null };
    placements?: Array<{ slug: string; bypassTemplateLocks?: boolean }>;
  },
): string[] {
  const out: string[] = [];
  if (!draft.placementSlug) out.push("No placement chosen");
  else if (
    config?.placements &&
    config.placements.length > 0 &&
    !config.placements.some((p) => p.slug === draft.placementSlug)
  ) {
    // Foreign slug carried in from a reused design - see isDraftComplete.
    out.push("No placement chosen");
  }
  if (!draft.mode) {
    out.push("No design type chosen");
    return out;
  }
  const bypass =
    !!config?.placements?.find((p) => p.slug === draft.placementSlug)
      ?.bypassTemplateLocks;
  const textRequired = !bypass && !!config?.product?.textRequired;
  // A bypass spot doesn't honour the template's locked font, so a non-
  // null lockedFontId can't satisfy the font check there. A Word Art
  // style pick satisfies it too - the preset is the face.
  const hasFont =
    !!draft.fontId ||
    !!draft.wordArtStyleId ||
    (!bypass && !!config?.product?.lockedFontId);
  // Upload-later: sending artwork later satisfies both the upload and
  // the logo-thread checks (threads get matched to the artwork later).
  const artworkMissing = !draft.uploadStagingKey && !draft.uploadLater;
  const logoThreadsMissing =
    draft.logoThreadCodes.length === 0 && !draft.uploadLater;
  if (draft.mode === "logo") {
    if (artworkMissing) out.push("No design uploaded");
    if (textRequired && !draft.text.trim()) out.push("No text entered");
    if (logoThreadsMissing) out.push("No thread colour chosen");
  } else if (draft.mode === "text") {
    if (!draft.text.trim()) out.push("No text entered");
    if (!hasFont) out.push("No font chosen");
    if (draft.textThreadCodes.length === 0) out.push("No thread colour chosen");
  } else if (draft.mode === "both") {
    if (artworkMissing) out.push("No design uploaded");
    if (!draft.text.trim()) out.push("No text entered");
    if (!hasFont) out.push("No font chosen");
    if (!draft.arrangement) out.push("No layout chosen");
    if (logoThreadsMissing) out.push("No design thread colour chosen");
    if (draft.textThreadCodes.length === 0) out.push("No text thread colour chosen");
  }
  return out;
}

// Sub-steps in flow order. Used to auto-confirm earlier steps so a
// Confirm press never bounces the customer back up the accordion.
const STEP_ORDER = [
  "kind",
  "placement",
  "mode",
  "upload",
  "text",
  "size",
  "arrangement",
  "threads",
  "threads-design",
  "threads-text",
  "outline",
];

// Phase B2: which sub-step the accordion should have open by default.
// Returns the id of the first unfinished step in flow order, or null
// when every step is finished. Since 2026-07-17 EVERY applicable step
// pauses for an explicit confirm - nothing auto-ticks itself.
function currentStepId(
  draft: EmbroideryDraft,
  config: Config,
  flags: {
    showKind: boolean;
    isSingleDesignFlow: boolean;
    /** Varsity / college style — adds an outline-colour step after the
     *  threads step. */
    isVarsity: boolean;
    /** Phase 67h: when true, the active placement opts out of the
     *  template-level size + arrangement locks, so those steps must
     *  appear in the flow even on an otherwise-locked template. */
    bypassLocks: boolean;
    /** Wave 1 (tap tax): true when text is a purely optional extra on a
     *  single-design flow. Blank optional text is treated as done, so
     *  the accordion never parks on a "Confirm text" the customer only
     *  needed if they wanted a name. Typing text re-engages the step. */
    textIsOptional: boolean;
    confirmed: Set<string>;
  },
): string | null {
  if (flags.showKind && !draft.mode) return "kind";
  if (!draft.placementSlug || !flags.confirmed.has("placement")) {
    return "placement";
  }
  if (!draft.mode) return "mode";
  if (
    (draft.mode === "logo" || draft.mode === "both") &&
    ((!draft.uploadStagingKey && !draft.uploadLater) ||
      !flags.confirmed.has("upload"))
  ) {
    return "upload";
  }
  // Text / font step — confirmed explicitly via a Confirm button.
  // The optional name-under-the-design step shows for a single-design
  // upload EXCEPT when the customer is on the pet/kid 3-tile picker and
  // explicitly chose "design only, no text" (mode stays "logo" there).
  const textVisible =
    draft.mode === "text" ||
    draft.mode === "both" ||
    // maxLines=0 (and textRequired off): template stitches no OPTIONAL
    // customer text - the lettering is the artwork - so the name-under-
    // the-design step neither renders nor gates. Scoped to this branch
    // only: a mode "text"/"both" draft (the sleeve-name upsell) still
    // needs the step, which is its only text/font editor.
    ((config.product.maxLines !== 0 || !!config.product.textRequired) &&
      flags.isSingleDesignFlow &&
      !!draft.uploadStagingKey &&
      !flags.showKind);
  if (textVisible && !flags.confirmed.has("text")) {
    // 2026-07-17 (Oscar): the optional pet/kid name no longer auto-skips
    // while blank. The step pauses so the customer actually decides -
    // type a name, or press Confirm to skip. Auto-ticking it forced a
    // back-click to add one and buried the personalisation moment.
    return "text";
  }
  // Size sits directly under the text / design step. The slider has no
  // natural "done" event, so the customer locks it in with Confirm.
  // The per-placement stage lock (cap year at 50mm, sock year at 60mm)
  // applies regardless of bypassLocks: it is scoped to one placement by
  // construction, so the all-or-nothing Phase 67h bypass has nothing to
  // protect it from.
  const sizeLocked =
    (!flags.bypassLocks && !!config.product.lockedSizeMm) ||
    !!stageLockedSizeMm(config.templateTag, draft.placementSlug);
  if (!sizeLocked && !flags.confirmed.has("size")) {
    return "size";
  }
  const arrangementLocked =
    !flags.bypassLocks && !!config.product.lockedArrangement;
  if (
    draft.mode === "both" &&
    !flags.isSingleDesignFlow &&
    !arrangementLocked &&
    (!draft.arrangement || !flags.confirmed.has("arrangement"))
  ) {
    return "arrangement";
  }
  // Thread colours are confirmed explicitly too (multi-pick has no
  // single "done" event).
  if (
    (draft.mode === "logo" || draft.mode === "text") &&
    !flags.confirmed.has("threads")
  ) {
    return "threads";
  }
  if (draft.mode === "both") {
    if (flags.isSingleDesignFlow) {
      if (!flags.confirmed.has("threads")) return "threads";
    } else {
      if (!flags.confirmed.has("threads-design")) return "threads-design";
      if (!flags.confirmed.has("threads-text")) return "threads-text";
    }
  }
  // Varsity / college: the arched text gets a stitched outline, so an
  // outline-colour step follows the thread colours.
  if (
    flags.isVarsity &&
    draft.text.trim() &&
    !flags.confirmed.has("outline")
  ) {
    return "outline";
  }
  return null;
}

// EST. year entry. Four single-digit boxes behind a fixed "EST." so the cap
// back reads the way it will be stitched, instead of a free-text area that
// invited three lines of anything. Emits the four digits as the draft text;
// the EST. itself is fixed art and is not part of what the customer types.
function YearBoxes({
  value,
  onChange,
  disabled,
  aboveLabel,
  prefix = "EST.",
  onComplete,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  /** Fixed line stitched ABOVE the year, e.g. BEST DAD on the sock cuff. */
  aboveLabel?: string;
  prefix?: string;
  /** 2026-07-24: fires when a REAL input event (typed or pasted) fills the
   *  4th digit. The 4th keystroke IS the answer, so demanding a separate
   *  "Confirm text" tap after it was pure tap tax. Never fired for a
   *  restored or initial value: both call sites live inside onInput, which
   *  only runs for genuine input, so this cannot pre-answer anything. */
  onComplete?: () => void;
}) {
  const digits = (value || "").replace(/\D/g, "").slice(0, 4).split("");
  const refs = React.useRef<Array<HTMLInputElement | null>>([]);
  const write = (i: number, d: string) => {
    const next = [0, 1, 2, 3]
      .map((n) => (n === i ? d : (digits[n] ?? "")))
      .join("")
      // Trailing blanks are trimmed so a partial year stays a short string
      // rather than "19  ", which would fail the non-empty text check oddly.
      .replace(/\s+$/, "");
    onChange(next);
    if (next.replace(/\D/g, "").length === 4) onComplete?.();
  };
  return (
    <div className="rk-pc-year-wrap">
      {aboveLabel && (
        <span className="rk-pc-year__above">{aboveLabel}</span>
      )}
      <div className="rk-pc-year">
      <span className="rk-pc-year__prefix">{prefix}</span>
      {[0, 1, 2, 3].map((i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className="rk-pc-year__box"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          aria-label={`Year digit ${i + 1}`}
          maxLength={1}
          disabled={disabled}
          value={digits[i] ?? ""}
          onInput={(e) => {
            const raw = (e.target as HTMLInputElement).value.replace(/\D/g, "");
            if (!raw) {
              write(i, "");
              return;
            }
            // Typing over a filled box, or pasting a whole year into one.
            if (raw.length > 1) {
              const all = raw.slice(0, 4 - i).split("");
              const nextStr = [0, 1, 2, 3]
                .map((n) =>
                  n >= i && all[n - i] ? all[n - i] : (digits[n] ?? ""),
                )
                .join("")
                .replace(/\s+$/, "");
              onChange(nextStr);
              refs.current[Math.min(3, i + all.length)]?.focus();
              // Pasting a whole year completes the answer the same as
              // typing its 4th digit.
              if (nextStr.replace(/\D/g, "").length === 4) onComplete?.();
              return;
            }
            write(i, raw);
            if (i < 3) refs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => {
            const k = (e as unknown as KeyboardEvent).key;
            if (k === "Backspace" && !digits[i] && i > 0) {
              refs.current[i - 1]?.focus();
            }
            if (k === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
            if (k === "ArrowRight" && i < 3) refs.current[i + 1]?.focus();
          }}
        />
      ))}
      </div>
    </div>
  );
}

export function PlacementSection({
  config,
  workerUrl,
  customerEmail,
  draft,
  onChange,
  onSave,
  onCancel,
  lockedSlugs,
  priorLogos,
  isEditingExisting,
  isAdditionalSpot,
  hasPriorSpots,
  onPlacementPicked,
  allowedSlugs,
}: {
  config: Config;
  workerUrl: string;
  customerEmail: string;
  draft: EmbroideryDraft;
  onChange: (next: EmbroideryDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  /** slug → name-of-spot-that-locked-it */
  lockedSlugs: Map<string, string>;
  /** logos uploaded on other (already-saved) spots, available for reuse here */
  priorLogos: PriorLogo[];
  /** true when the draft was repopulated from an existing spot (Edit) */
  isEditingExisting: boolean;
  /** Phase 39b: true when the customer's been here before in this same
   *  session and is adding a new spot on top. On pet templates this
   *  flips the editor to a "pet drawing or just text?" mini-picker. */
  isAdditionalSpot: boolean;
  /** Phase 67e: true when at least one spot is already saved. Drives the
   *  progressive-disclosure filter that hides `requiresAnyPriorSpot`
   *  placements (e.g. sleeves) until the primary spot is placed. */
  hasPriorSpots: boolean;
  /** Wave 1 funnel: fired once per editor session, on the customer's
   *  FIRST tap of a placement card (never on the single-placement
   *  auto-pick). App uses it for rk_placement_picked + the deferred
   *  rk_design_started when the editor was auto-opened on load. */
  onPlacementPicked?: (slug: string) => void;
  /** Garment stages (see ../stages.ts): when set, the picker only offers
   *  these slugs, so "design the hoodie" cannot wander onto the sock. Null
   *  or undefined on every single-garment template, which keeps the normal
   *  full-list behaviour. */
  allowedSlugs?: string[] | null;
}) {
  // Stage scoping. Filtering here rather than at every read site means the
  // auto-pick-when-only-one-placement path, the excludes logic and the
  // progressive-disclosure filter all inherit it for free.
  const placements =
    allowedSlugs && allowedSlugs.length > 0
      ? config.placements.filter((p) => allowedSlugs.includes(p.slug))
      : config.placements;
  const fonts = config.fonts;
  // Word Art (2026-07): an additive style gallery that REPLACES the font
  // picker on Word Art templates. Enabled presets come from
  // config.wordArtStyles (emitted by the config resolver for the Word Art
  // template). A ?rk_wordart=1 query param force-enables every preset for a
  // dev preview on any test product, so Phase 1 can be exercised before the
  // resolver emits the field. Empty on every existing product, so nothing
  // else changes.
  const wordArtStyles: WordArtPreset[] = (() => {
    const ids = config.wordArtStyles;
    if (ids && ids.length) {
      return ids
        .map((id) => WORD_ART_PRESETS.find((p) => p.id === id))
        .filter((p): p is WordArtPreset => !!p);
    }
    // The dedicated Word Art template enables the whole launch set by tag,
    // so no schema change / resolver edit is needed to light it up. Every
    // other template leaves this empty and keeps the normal font picker.
    if (config.templateTag === WORD_ART_TEMPLATE_TAG) return WORD_ART_PRESETS;
    if (
      typeof location !== "undefined" &&
      /[?&]rk_wordart=1\b/.test(location.search)
    ) {
      return WORD_ART_PRESETS;
    }
    return [];
  })();
  const wordArtMode = wordArtStyles.length > 0;
  const activePlacement = draft.placementSlug
    ? placements.find((p) => p.slug === draft.placementSlug) ?? null
    : null;
  const set = (patch: Partial<EmbroideryDraft>) => onChange({ ...draft, ...patch });
  const complete = isDraftComplete(draft, config);
  // RK26682 hardening: true while the artwork drop zone has an upload in
  // flight. "Update spot" stays disabled until it lands, otherwise a fast tap
  // commits the PREVIOUS file (the draft still holds the old staging key).
  const [uploadBusy, setUploadBusy] = React.useState(false);

  // Phase B2: accordion state. `manualStep` is the sub-step the customer
  // last clicked (null = follow the natural flow; "" = collapse all).
  const [manualStep, setManualStep] = React.useState<string | null>(null);
  const prevCurrentStep = React.useRef<string | null>(null);
  // Phase B2: ref on the editor body so the open sub-step can be found +
  // scrolled into view when the flow advances.
  const editorBodyRef = React.useRef<HTMLDivElement>(null);
  // Phase B2: steps the customer has explicitly confirmed. Size + thread
  // colours have no natural "done" event, so they each carry a Confirm
  // button. Editing an existing spot starts them all confirmed so the
  // accordion opens fully collapsed.
  const [confirmed, setConfirmed] = React.useState<Set<string>>(() => {
    if (isEditingExisting) {
      return new Set([
        "placement",
        "upload",
        "text",
        "size",
        "arrangement",
        "threads",
        "threads-design",
        "threads-text",
        "outline",
      ]);
    }
    // CRO 3.1 (tap tax): a draft that arrives pre-placed (template
    // default, reused design, sleeve cross-sell) starts with the
    // placement ticked so the accordion opens on the content. The
    // collapsed header's "Change" keeps the picker one tap away.
    //
    // 2026-07-10 (Oscar): size is NOT pre-confirmed any more. The old C6
    // seed skipped the "How big?" step entirely (straight to threads),
    // which glossed over the clearest upsell in the flow - the size tier
    // ladder (bigger mm = higher tier price). The step now opens in the
    // natural flow with the slider + ladder visible and its explicit
    // Confirm. 2026-07-17 (Oscar): the "nothing to sell stays tap-free"
    // carve-out is gone too - size pauses on EVERY placement.
    const seed = draft.placementSlug ? ["placement"] : [];
    return new Set(seed);
  });
  // True when a sub-step has enough content that collapsing it should
  // also tick it complete, so the flow advances without a Confirm click.
  const stepContentReady = (id: string): boolean => {
    switch (id) {
      case "placement":
        return !!draft.placementSlug;
      case "kind":
      case "mode":
        return !!draft.mode;
      case "upload":
        return !!draft.uploadStagingKey || !!draft.uploadLater;
      case "text":
        if (draft.mode === "text" || draft.mode === "both") {
          return (
            !!draft.text.trim() &&
            (wordArtMode ? !!draft.wordArtStyleId : !!draft.fontId)
          );
        }
        // 2026-07-17: mode null / "logo" - the text step isn't applicable
        // YET, which is not the same as done. Returning true here let the
        // confirm cascade pre-tick an empty text step the moment size
        // auto-confirmed on a no-paid-tier placement; the customer then
        // picked "Text" and met an all-ticked accordion with nothing open
        // (the threads substep can't render without text+font). And a
        // blank optional pet/kid name must not sweep-tick either - Oscar
        // wants that step to pause until its own Confirm (type or skip).
        return false;
      case "size":
        // 2026-07-17 (Oscar): never content-ready. The slider always
        // carries a valid default, but "has a default" is not "customer
        // chose" - only the explicit Confirm ticks it, so the later-step
        // cascade and a collapse can't silently skip the size pause.
        return false;
      case "arrangement":
        return !!draft.arrangement;
      case "threads":
      case "threads-design":
        return draft.logoThreadCodes.length > 0;
      case "threads-text":
        return draft.textThreadCodes.length > 0;
      case "outline":
        return !!draft.outlineThreadCode;
      default:
        return false;
    }
  };

  // Wave 1 funnel: once-per-editor-session guards for the step events.
  // The editor mounts fresh per spot (App unmounts it when draft closes),
  // so "once per mount" is "once per spot".
  const placementPickedFiredRef = React.useRef(false);
  const uploadDoneFiredRef = React.useRef(false);
  const threadsDoneFiredRef = React.useRef(false);
  const sizeDoneFiredRef = React.useRef(false);
  // Pet-photo trap: rk_photo_detected fires at most once per spot, same
  // once-per-mount pattern as the wave-1 funnel refs above.
  const photoDetectedFiredRef = React.useRef(false);

  const confirmStep = (id: string) => {
    // Wave 1 funnel: the thread step confirms via the swatch tap
    // (pickThread) now that the decoy Confirm buttons are gone, so this
    // is the single chokepoint for "threads done". Editing an existing
    // spot starts all-confirmed and never re-fires.
    if (
      !threadsDoneFiredRef.current &&
      !isEditingExisting &&
      (id === "threads" || id === "threads-design" || id === "threads-text")
    ) {
      threadsDoneFiredRef.current = true;
      emitAnalytics("threads_done", {
        placementSlug: draft.placementSlug,
        mode: draft.mode,
        flowKind: flowKindFor(config),
      });
    }
    if (!sizeDoneFiredRef.current && !isEditingExisting && id === "size") {
      sizeDoneFiredRef.current = true;
      emitAnalytics("size_done", {
        placementSlug: draft.placementSlug,
        mode: draft.mode,
        flowKind: flowKindFor(config),
      });
    }
    setConfirmed((prev) => {
      const next = new Set(prev);
      next.add(id);
      // Don't bounce the customer backwards: when they confirm a step,
      // treat every earlier step that already has its content as done
      // too, so the accordion never jumps back up to an un-ticked step
      // above (e.g. a placement that was picked but not Confirmed).
      const idx = STEP_ORDER.indexOf(id);
      for (let i = 0; i < idx; i++) {
        if (stepContentReady(STEP_ORDER[i])) next.add(STEP_ORDER[i]);
      }
      return next;
    });
    // Always hand control back to the natural flow so a Confirm press
    // collapses this step and opens the next — even when re-confirming a
    // step that was already ticked.
    setManualStep(null);
  };

  // Phase 67: template-level locks. When the template forces a specific
  // font / arrangement, mirror them onto the draft as soon as the
  // editor opens so the customer's draft has the locked values without
  // needing to click anything. Also re-apply if the locks change at
  // runtime (e.g. variant switch picking up a new template tag).
  //
  // Phase 67h: a placement may opt out of these locks
  // (`bypassTemplateLocks`). For that spot the customer gets the
  // standard customiser (free font / size / arrangement, no varsity
  // outline) even though the template carries locks for the other
  // spots. Used by the pet-varsity BACK placement.
  const bypassLocks = !!activePlacement?.bypassTemplateLocks;
  const lockedFontId = bypassLocks ? null : config.product.lockedFontId ?? null;
  const lockedArrangement = bypassLocks
    ? null
    : config.product.lockedArrangement ?? null;
  // Per-placement stage lock first (dead-question rule: a 50mm cap panel is
  // not a size decision), then the template-wide lock. Everything downstream
  // (the sizeMm seeding effect, the substep gate, the read-only "Stitched at
  // Nmm" line) hangs off this one value.
  const lockedSizeMm =
    stageLockedSizeMm(config.templateTag, draft.placementSlug) ??
    (bypassLocks ? null : config.product.lockedSizeMm ?? null);
  const lockedAddonStyle = bypassLocks
    ? null
    : config.product.lockedAddonStyle ?? null;
  const textRequired = bypassLocks ? false : !!config.product.textRequired;
  React.useEffect(() => {
    const patch: Partial<EmbroideryDraft> = {};
    if (lockedFontId && draft.fontId !== lockedFontId) {
      patch.fontId = lockedFontId;
    }
    if (lockedArrangement && draft.arrangement !== lockedArrangement) {
      patch.arrangement = lockedArrangement as EmbroideryDraft["arrangement"];
    }
    // Phase 67c: pin the size to the locked value, and force the
    // addon style. The draft validation already accepts these so save
    // gates work without customer interaction.
    if (lockedSizeMm && draft.sizeMm !== lockedSizeMm) {
      patch.sizeMm = lockedSizeMm;
    }
    if (lockedAddonStyle) {
      const wantColoured = lockedAddonStyle === "coloured";
      if (draft.addonStyleColoured !== wantColoured) {
        patch.addonStyleColoured = wantColoured;
      }
    }
    // Phase 67d: text-required on a pet/kid flow → draft mode should
    // be "both" out of the gate so the text input is visible BEFORE
    // the customer has uploaded a photo. Without this, the text input
    // only renders once an upload exists (the legacy single-design
    // visibility rule); with textRequired the customer needs to see
    // the input regardless.
    if (
      textRequired &&
      isSingleDesignFlow &&
      draft.mode !== "both" &&
      draft.mode !== "text"
    ) {
      patch.mode = "both";
      if (!patch.arrangement && lockedArrangement) {
        patch.arrangement = lockedArrangement as EmbroideryDraft["arrangement"];
      }
    }
    if (Object.keys(patch).length > 0) set(patch);
    // `draft.fontId` is in the deps on purpose. A locked font suppresses
    // the font picker entirely, so nothing else ever writes draft.fontId.
    // If the draft is (re)created after this effect's first run - a fresh
    // spot, a cancel, a reuse-decode - fontId lands back on null and the
    // locked font is silently lost. That matters because the text thread
    // substep gates on `draft.fontId || draft.wordArtStyleId`, so an
    // unseeded font makes the thread picker never render and add-to-cart
    // stays blocked with "No thread colour chosen" and no way to fix it.
    // Re-running on fontId changes makes the seeding self-healing; it
    // cannot loop, because the patch is only built while the value
    // differs from the lock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedFontId, lockedArrangement, lockedSizeMm, lockedAddonStyle, draft.placementSlug, draft.fontId, bypassLocks]);

  // Phase 67: lockedMaxMm clamps the placement's maxWidthMm to the
  // template-level cap (e.g. pet-varsity = 300mm everywhere).
  // Phase 67h: a placement that bypasses template locks (e.g. the
  // pet-varsity back) ignores the cap so the customer can size
  // normally on it. Shared by pickPlacement + the clamp effect below so
  // the two can never disagree on what "too big" means.
  const effectiveCapFor = (p: Placement): number => {
    const applyMaxCap = !p.bypassTemplateLocks && !!config.product.lockedMaxMm;
    return applyMaxCap
      ? Math.min(p.maxWidthMm, config.product.lockedMaxMm as number)
      : p.maxWidthMm;
  };

  // Audit 2026-10: drafts that arrive with a preset slug never pass
  // through pickPlacement - the sleeve cross-sell, a reused design, a
  // restored envelope - so emptyDraft's 80mm rode straight onto a 60mm
  // sleeve. Clamp to the active placement's effective cap whenever the
  // slug or the size changes. Locked-size templates are left to the
  // seeding effect above (it pins sizeMm to the lock and hides the
  // slider); clamping there too could fight it if a lock ever exceeded
  // a placement cap. Cannot loop: it only writes while sizeMm > cap.
  React.useEffect(() => {
    if (!activePlacement || lockedSizeMm) return;
    const cap = effectiveCapFor(activePlacement);
    if (cap > 0 && draft.sizeMm > cap) set({ sizeMm: cap });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.placementSlug, draft.sizeMm, lockedSizeMm]);

  const pickPlacement = (p: Placement, viaAuto = false) => {
    const cap = effectiveCapFor(p);
    // The size carried into a placement is the customer's current size
    // (emptyDraft seeds 80mm), clamped to this placement's cap. The admin's
    // per-placement defaultWidthMm is deliberately NOT used as the seed:
    // the live templates set centre-front to 180mm and back to 220mm, which
    // on the master ladder would open a fresh spot at +$19 / +$25 instead
    // of free (reviewed 2026-10-21). It is only the fallback when the
    // carried size does not fit the cap (or is 0), so a sleeve capped at
    // 60mm lands on the sleeve's own default rather than an arbitrary clamp.
    const carried = Math.min(draft.sizeMm, cap);
    const fallback = Math.min(p.defaultWidthMm || cap, cap);
    const cappedSize = carried > 0 ? carried : fallback;
    // W4 phase 2: a dragged preview position is relative to ONE
    // placement's calibrated photo zone - switching placement recentres.
    set({
      placementSlug: p.slug,
      sizeMm: cappedSize,
      previewOffset: null,
    });
    // Single-select: picking a spot IS confirming it. Collapse + advance
    // without the old "Confirm spot" tap.
    confirmStep("placement");
    // 2026-07-17 (Oscar): size ALWAYS pauses - the tap-free auto-tick for
    // placements with nothing paid in reach is gone. An auto-ticked "How
    // big?" made customers back-click a CHANGE to resize, and buried the
    // tier ladder upsell where one exists. Switching to a different spot
    // re-opens the step (the new cap re-clamps the size); editing an
    // existing spot stays fully confirmed.
    if (!isEditingExisting && p.slug !== draft.placementSlug) {
      setConfirmed((prev) => {
        if (!prev.has("size")) return prev;
        const next = new Set(prev);
        next.delete("size");
        return next;
      });
    }
    // Wave 1 funnel: a real tap on a placement card (never the single-
    // placement auto-pick) is the customer's first deliberate design
    // action. Fired once per editor session; new spots only.
    if (!viaAuto && !isEditingExisting && !placementPickedFiredRef.current) {
      placementPickedFiredRef.current = true;
      onPlacementPicked?.(p.slug);
    }
  };

  // C6 (tap tax): the thread pickers are single-select (max=1), so the tap
  // that picks the colour IS the confirmation — mirror pickPlacement and
  // auto-advance instead of making the customer press "Confirm colour".
  // `apply` writes the draft; we only advance when a colour actually landed
  // (deselecting back to empty leaves the step open so they can re-pick).
  const pickThread = (
    stepId: string,
    apply: () => void,
    hasSelection: boolean,
  ) => {
    apply();
    if (hasSelection) confirmStep(stepId);
  };

  // CRO 3.1 (tap tax): placement slugs that arrive from OUTSIDE the picker
  // after mount (the config-load effect pre-selecting the template
  // default, a restored draft) count as done too. Taps inside the picker
  // confirm via pickPlacement.
  React.useEffect(() => {
    if (!draft.placementSlug) return;
    setConfirmed((prev) => {
      if (prev.has("placement")) return prev;
      const next = new Set(prev);
      next.add("placement");
      return next;
    });
  }, [draft.placementSlug]);

  // 2026-07-17: belt-and-braces for the pre-tick trap above. Switching
  // INTO a text-bearing mode revokes an unearned "text" tick (restored
  // drafts / sessions from before the stepContentReady fix), so the
  // accordion opens on the text input instead of skipping past it.
  React.useEffect(() => {
    if (draft.mode !== "text" && draft.mode !== "both") return;
    if (stepContentReady("text")) return;
    setConfirmed((prev) => {
      if (!prev.has("text")) return prev;
      const next = new Set(prev);
      next.delete("text");
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.mode]);

  // When exactly one placement is on offer there's no choice to make -
  // pick it so the flow opens straight on the design content.
  React.useEffect(() => {
    if (draft.placementSlug) return;
    const pickable = placements.filter((p) => {
      if (p.requiresAnyPriorSpot && !hasPriorSpots) return false;
      return !lockedSlugs.has(p.slug);
    });
    if (pickable.length === 1) pickPlacement(pickable[0], true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.placementSlug, placements, hasPriorSpots]);

  // Wave 1 funnel: the size step nearly always auto-seeds a valid default
  // (emptyDraft's 80mm / a template lock), so rk_size_done fires when the
  // step first becomes relevant with size already confirmed, not on a
  // press the customer rarely makes. confirmStep("size") covers the
  // explicit path; the shared ref keeps it to one fire per spot.
  React.useEffect(() => {
    if (sizeDoneFiredRef.current || isEditingExisting) return;
    if (!activePlacement || !draft.mode) return;
    if (!confirmed.has("size")) return;
    sizeDoneFiredRef.current = true;
    emitAnalytics("size_done", {
      placementSlug: draft.placementSlug,
      mode: draft.mode,
      flowKind: flowKindFor(config),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlacement, draft.mode, confirmed]);

  // Phase 39b: on pet templates, additional spots offer a 2-tile picker
  // — pet drawing again, or text-only (e.g. just the pet's name on a
  // sleeve). The first spot on a pet template always uses the pet
  // flow (no choice needed); editing an existing spot keeps that
  // spot's existing type. Only fresh additional spots see the picker.
  // Phase 70: flow kind + design upsell now come from the bound
  // FlowBlueprint (with fallback to the legacy magic-kind lookup
  // during the migration window).
  const flow = flowKindFor(config);
  const isPetFlow = flow === "pet";
  const isKidFlow = flow === "kid";
  // Phase 68b: line-drawing of a person flow. Structurally identical
  // to pet (multi-image upload supported for couples / families /
  // groups, same coloured + filled toggle); fork only on user-facing
  // copy.
  const isPersonFlow = flow === "person";
  // Phase 43a / 68b: pet + kid + person behave the same structurally
  // - single design upload, optional text under it, single thread
  // picker, additional spots offer "Design / Design + name / Just
  // text". We gate the shared UX on this flag, then fork the
  // user-facing copy per flow.
  const isSingleDesignFlow = isPetFlow || isKidFlow || isPersonFlow;
  // maxLines=0 means the template stitches no customer text anywhere -
  // the lettering IS the artwork (e.g. DAD varsity: drawing inside the
  // "A"). Suppresses the optional name editor and the "+ name" / "Just
  // text" tiles so the flow reads as pure photo upload. textRequired
  // wins over it: a contradictory template (text mandatory but the step
  // suppressed) would deadlock Save, so the disable only applies when
  // text is optional.
  const templateTextDisabled =
    config.product.maxLines === 0 && !config.product.textRequired;
  // Varsity / college style: a pet flow that locks the text above the
  // design (rendered arched in the visualiser). It adds an extra step
  // for the arched text's stitched outline colour.
  //
  // Phase 67h: spots that bypass template locks (e.g. the pet-varsity
  // BACK placement) render with the standard layout and skip the
  // outline step regardless of the template's varsity status.
  const isVarsity =
    isPetFlow &&
    config.product.lockedArrangement === "above" &&
    !bypassLocks;
  const flowUpsellId = designUpsellFor(config)?.id ?? null;
  // Pet-photo trap (2026-07-12): the LOGO flow's in-place "Upgrade to
  // Portrait" offer. Null on non-logo flows or when the blueprint has
  // no photoUpgradeUpsellId wired, which also disables detection.
  const photoUpgrade = photoUpgradeUpsellFor(config);
  const showPetOrTextPicker =
    isSingleDesignFlow &&
    isAdditionalSpot &&
    !isEditingExisting &&
    !templateTextDisabled;
  // Phase 42b + 43a: 3-tile picker on additional spots (pet OR kid).
  // Selection state is derived from the draft so the picker reflects
  // the customer's last choice when they edit back.
  const isDesignOnly =
    !!draft.addonUpsellId &&
    draft.addonUpsellId === flowUpsellId &&
    draft.mode === "logo";
  const isDesignWithText =
    !!draft.addonUpsellId &&
    draft.addonUpsellId === flowUpsellId &&
    draft.mode === "both";
  const isTextOnly = !draft.addonUpsellId && draft.mode === "text";

  // Phase 79: keep the thread codes in lockstep with the drawing-style
  // choice on single-design flows (pet / kid / person).
  //   - "Coloured + filled in" (addonStyleColoured=true): the design's
  //     own colours determine the threads. Auto-fill logoThreadCodes +
  //     textThreadCodes with the MATCH_DESIGN_CODE sentinel so the
  //     substep auto-completes + the saved-spot summary reads "Match
  //     the design". The customer can still tap a swatch below to
  //     override to a single thread (the ThreadPicker keeps its match
  //     option + swatch grid pattern intact).
  //   - "Line drawing" (default): the whole design stitches in one
  //     solid colour. Clear MATCH_DESIGN_CODE if it's lingering from a
  //     style flip-flop, so the swatch picker is empty + the
  //     StepConfirm gates until the customer picks a real colour.
  // Runs only on isSingleDesignFlow; the logo flow's thread picking
  // is untouched. Locked addonStyle from the template (handled by the
  // earlier locked-state effect) flows into draft.addonStyleColoured
  // and triggers this effect on the next render - no special-casing.
  React.useEffect(() => {
    if (!isSingleDesignFlow) return;
    const hasMatch = draft.logoThreadCodes.includes(MATCH_DESIGN_CODE);
    if (draft.addonStyleColoured && !hasMatch) {
      set({
        logoThreadCodes: [MATCH_DESIGN_CODE],
        textThreadCodes: [MATCH_DESIGN_CODE],
      });
      // Wave 1 (tap tax): the auto-filled "Match the design" is a valid
      // default, so confirm the threads step like the size seed does -
      // with the decoy Confirm button gone there'd otherwise be nothing
      // to tap. The step stays collapsed-but-openable ("Change") for
      // anyone who'd rather stitch a single colour.
      confirmStep("threads");
    } else if (
      draft.addonStyleColoured &&
      hasMatch &&
      !confirmed.has("threads")
    ) {
      // Restored draft (refresh / variant remount) already carrying the
      // match sentinel - same valid default, same auto-confirm, so the
      // step can't park open with nothing to tap.
      confirmStep("threads");
    } else if (!draft.addonStyleColoured && hasMatch) {
      set({ logoThreadCodes: [], textThreadCodes: [] });
      // Flipping back to line drawing clears the codes, so a colour is
      // required again - un-confirm so the accordion re-opens on the
      // swatch picker instead of skipping a now-unfinished step.
      setConfirmed((prev) => {
        if (!prev.has("threads")) return prev;
        const next = new Set(prev);
        next.delete("threads");
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSingleDesignFlow, draft.addonStyleColoured]);

  // 2026-08-03 (RK24457): the Phase 79 sync above only covers single-design
  // flows, but a style-governed drawing add-on can ALSO ride on the logo
  // flow (e.g. the FD gift box's "Their drawing" scan). With the style on
  // "Line drawing" the design stitches in ONE colour, so "Match the design
  // (colour-matched at digitising)" is nonsense there - yet the logo flow's
  // design-thread pickers offered it unconditionally and orders shipped with
  // the contradiction on the line. Gate the tile, and strip a lingering
  // match sentinel so the picker re-gates on a real swatch.
  const activeSpotAddon = draft.addonUpsellId
    ? config.upsells?.find((x) => x.id === draft.addonUpsellId)
    : null;
  // 2026-08-14: style-governed = the add-on HAS a style axis, including a
  // zeroed one (styleSurchargeCents 0 = "coloured included in the garment
  // price", the sanctioned free-style config). `!= null` — NOT truthiness —
  // so a surcharge-0 template still gates "Match the design" off line
  // drawings (RK24457) and still counts as covering the match fee below.
  const addonStyleGoverned = activeSpotAddon?.styleSurchargeCents != null;
  const designMatchAllowed = !addonStyleGoverned || draft.addonStyleColoured;
  // 2026-08-14: "Match the design" now carries the coloured + filled fee
  // (see pricing.matchDesignSurchargeCentsFor). The tile must SHOW the
  // price wherever picking it would charge — that's every spot whose
  // add-on style axis isn't governing the fee. Mirrors the pricing dedupe
  // exactly so the tile never advertises a fee the total wouldn't add
  // (or vice versa).
  const matchFeeCovered = addonStyleGoverned && draft.addonStyleColoured;
  const matchPriceCents = matchFeeCovered
    ? undefined
    : MATCH_DESIGN_SURCHARGE_CENTS;
  React.useEffect(() => {
    if (designMatchAllowed || isSingleDesignFlow) return;
    if (!draft.logoThreadCodes.includes(MATCH_DESIGN_CODE)) return;
    set({ logoThreadCodes: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designMatchAllowed, isSingleDesignFlow, draft.logoThreadCodes]);

  // Flow-specific copy. Pet says "pet's"; kid says "kid's"; person
  // says "their" (the only grammatical fit for "stitch the X name").
  const designSubjectPossessive = isPetFlow
    ? "pet's"
    : isKidFlow
      ? "kid's"
      : "their";
  // Phase 68b: noun used for the "extra X" hint on multi-image uploads
  // (pet flow: "pet"; person flow: "person"). Kid flow doesn't use
  // multi-image upload so it's never asked.
  const extraSubjectSingular = isPersonFlow ? "person" : "pet";
  const extraSubjectPlural = isPersonFlow ? "people" : "pets";

  const pickDesign = (withText: boolean) => {
    if (!flowUpsellId) return;
    const u = config.upsells?.find((up) => up.id === flowUpsellId);
    set({
      mode: withText ? "both" : "logo",
      addonUpsellId: flowUpsellId,
      addonCount: u?.countMax && u.countMax > 1 ? 1 : null,
      addonStyleColoured: false,
      arrangement: withText ? "below" : null,
      // "Pet design + name" keeps any typed name so toggling tiles
      // doesn't lose it. "Pet design" means design only, NO text — clear
      // any leftover name + font so nothing stitches that the customer
      // explicitly opted out of.
      ...(withText ? {} : { text: "", fontId: null }),
    });
  };
  // Text-disabled templates skip the 3-tile "What's on this spot?"
  // picker (design is the only real option), so prime the design mode
  // + flow upsell the same way a tile tap would. Mirrors the standard
  // flow's single-option force below - a one-answer question is a dead
  // question, not a step to confirm.
  React.useEffect(() => {
    if (!templateTextDisabled || !isSingleDesignFlow) return;
    if (!isAdditionalSpot || isEditingExisting) return;
    if (draft.mode) return;
    if (flowUpsellId) {
      pickDesign(false);
    } else {
      // Misconfigured blueprint (no design upsell wired): pickDesign
      // would silently no-op and the sequencer would park on a "mode"
      // step this flow never renders. Fall back to a bare logo mode so
      // the upload step still appears.
      set({ mode: "logo" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateTextDisabled, isAdditionalSpot, isEditingExisting, draft.mode]);

  // The patch lives in ../textDraft.ts (pure, unit tested) so the
  // "Just text" pick and the typing rule in applyTextChange below are
  // verified together: a text-only spot must stay text-only.
  const pickTextOnly = () => {
    set(textOnlyPatch());
  };

  // Pet-photo trap: the two choices on the "That looks like a photo!"
  // card. Accept attaches the photo-upgrade upsell to this spot exactly
  // like pickDesign wires the pet flow's design upsell - the existing
  // addonUpsellId machinery then prices it (base + coloured-and-filled
  // surcharge, promo waivers via the effective* helpers) and emits the
  // "Spot N add-on" cart property. Decline remembers per-spot so the
  // card never re-nags this upload.
  const acceptPhotoUpgrade = () => {
    if (!photoUpgrade || draft.addonUpsellId === photoUpgrade.id) return;
    set({
      addonUpsellId: photoUpgrade.id,
      addonCount:
        photoUpgrade.countMax && photoUpgrade.countMax > 1 ? 1 : null,
      // Portraits are the coloured + filled treatment - that's the whole
      // point of the upgrade. The style toggle below stays available if
      // the customer would rather drop to a line drawing.
      addonStyleColoured: true,
    });
    emitAnalytics("photo_upgrade_taken", {
      placementSlug: draft.placementSlug,
      mode: draft.mode,
      flowKind: flow,
      priceCents:
        effectiveUpsellPriceCents(photoUpgrade, config) +
        effectiveUpsellStyleSurchargeCents(photoUpgrade, config),
    });
  };
  const declinePhotoUpgrade = () => {
    if (draft.photoUpgradeDeclined) return;
    set({ photoUpgradeDeclined: true });
    emitAnalytics("photo_upgrade_declined", {
      placementSlug: draft.placementSlug,
      mode: draft.mode,
      flowKind: flow,
    });
    // The card was the only thing holding the upload step open (see the
    // onUploaded auto-advance gate) - declining collapses it and the
    // flow advances exactly as it would have without the offer.
    if (!uploadAddonConfigVisible) confirmStep("upload");
  };

  // Phase B2: per-step completeness + collapsed-summary helpers.
  const threadSummary = (codes: string[]) =>
    codes
      .map((c) =>
        c === MATCH_DESIGN_CODE
          ? "Match the design"
          : config.threads.find((t) => t.code === c)?.name ?? c,
      )
      .join(", ");
  // `textRequired` already declared above with bypassLocks applied.
  const textIsOptional =
    isSingleDesignFlow && !textRequired && draft.mode !== "text";
  const textComplete = textIsOptional
    ? true
    : !!draft.text.trim() &&
      (wordArtMode
        ? !!draft.wordArtStyleId
        : !!draft.fontId || !!lockedFontId);

  // Pet-photo trap: honest expectation-setting for a photo-like upload
  // the customer chose NOT to upgrade - "Match the design" on a photo
  // means a few main colours, not a full-colour reproduction. Only the
  // declined case gets the clause; an upgraded spot is genuinely
  // colour-matched by the artist redraw.
  const photoMatchHintExtra =
    draft.uploadPhotoLike && draft.photoUpgradeDeclined
      ? "(for photos we match a few main colours - fine detail becomes simple shapes)"
      : undefined;

  // Wave 1 (tap tax): does the upload substep render extra add-on config
  // UI under the dropzone (pet/person multi-photo adder, count stepper,
  // line/coloured style toggle)? Mirrors the render gate of the
  // addon-config block inside the upload Substep below - keep in sync.
  // When there's NO extra UI, a successful upload IS the confirmation:
  // the step auto-confirms and the decoy "Confirm design" button is
  // dropped. When add-on UI is present the explicit Confirm stays, so
  // the customer isn't advanced past choices they haven't seen.
  const uploadAddonConfigVisible = (() => {
    const u = draft.addonUpsellId
      ? config.upsells?.find((x) => x.id === draft.addonUpsellId)
      : null;
    if (!u) return false;
    const supportsCount = !!u.countMax && u.countMax > 1;
    const supportsStyle = !!u.styleSurchargeCents && !lockedAddonStyle;
    return supportsCount || supportsStyle;
  })();

  // Derive the open accordion step: the natural flow opens the first
  // unfinished step; a manual click overrides it until the flow moves on.
  const currentStep = currentStepId(draft, config, {
    showKind: showPetOrTextPicker,
    isSingleDesignFlow,
    isVarsity,
    bypassLocks,
    textIsOptional,
    confirmed,
  });
  React.useEffect(() => {
    if (prevCurrentStep.current !== currentStep) {
      prevCurrentStep.current = currentStep;
      setManualStep(null);
    }
  });
  const openStep =
    manualStep === null ? currentStep : manualStep === "" ? null : manualStep;

  const toggleStep = (id: string) => {
    if (openStep === id) {
      // Collapsing the open step. If the customer has filled it in, tick
      // it like a Confirm press so the accordion flows on to the next
      // step without needing the extra click. Left untouched (just
      // closed) when the content isn't ready yet.
      if (stepContentReady(id)) {
        confirmStep(id);
        setManualStep(null);
      } else {
        setManualStep("");
      }
    } else {
      setManualStep(id);
    }
  };

  // Phase B2: whenever the open sub-step changes, ease it toward the
  // middle of the screen so the customer follows the flow (works even
  // when the next step mounts already-open). Skips the first render —
  // that's the editor opening, which App's own scroll handles.
  const firstStepScroll = React.useRef(true);
  React.useEffect(() => {
    if (firstStepScroll.current) {
      firstStepScroll.current = false;
      return;
    }
    // 2026-07-24 mobile scroll policy: on a phone this effect does not run
    // at all. Every auto-advance (tile tap, thread pick, and worst of all
    // the ASYNC upload-success confirm, which landed seconds after the tap)
    // changed openStep and fired a 560ms scroll concurrent with two 400ms
    // accordion transitions. Measured on the live FD bundle at 375px: one
    // "Add the year" tap moved the page 684px. Oscar's report ("scrolls all
    // around automatically, almost never to the correct place") is this
    // effect. On a small screen the opening substep is nearly always
    // adjacent to the tap anyway; the util's already-visible skip covers
    // desktop, and mobile customers keep control of their own page.
    if (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 640px)").matches
    ) {
      return;
    }
    // Wave 1: never auto-scroll before the customer's first interaction
    // (see the pageHasInteracted latch above) - the auto-opened editor's
    // auto-picked placement would otherwise yank the page on load.
    if (!pageHasInteracted) return;
    const root = editorBodyRef.current;
    if (!root) return;
    if (!openStep) {
      // Every sub-step is done + collapsed (e.g. the customer just
      // confirmed the last thread colour). The editor is now short —
      // ease it back up so they land on the Save button instead of
      // being stranded far down the page.
      easedScrollTo(root, 90, 560);
      return;
    }
    const el = root.querySelector<HTMLElement>(
      `[data-substep="${openStep}"]`,
    );
    if (el) {
      easedScrollTo(el, Math.round((window.innerHeight || 800) * 0.4), 560);
    }
  }, [openStep]);

  return (
    <div className="rk-pc-sec__body" ref={editorBodyRef}>
      {showPetOrTextPicker && (
        <Substep
          id="kind"
          label="What's on this spot?"
          complete={!!draft.mode}
          summary={
            isDesignWithText
              ? isPetFlow
                ? "Pet design + name"
                : isKidFlow
                  ? "Kid's drawing + name"
                  : "Portrait + name"
              : isDesignOnly
                ? isPetFlow
                  ? "Pet design"
                  : isKidFlow
                    ? "Kid's drawing"
                    : "Portrait"
                : isTextOnly
                  ? "Just text"
                  : ""
          }
          openStep={openStep}
          onToggle={toggleStep}
        >
          <div className="rk-pc-mode-row rk-pc-mode-row--three">
            <button
              type="button"
              className={`rk-pc-mode-tile${isDesignOnly ? " is-selected" : ""}`}
              onClick={() => pickDesign(false)}
            >
              <span className="rk-pc-mode-tile__title">
                {isPetFlow ? "Pet design" : isKidFlow ? "Kid's drawing" : "Portrait"}
              </span>
              <span className="rk-pc-mode-tile__desc">
                {isPetFlow
                  ? "Upload a photo of your pet"
                  : isKidFlow
                    ? "Upload their drawing"
                    : "Upload a photo of them"}
              </span>
            </button>
            <button
              type="button"
              className={`rk-pc-mode-tile${isDesignWithText ? " is-selected" : ""}`}
              onClick={() => pickDesign(true)}
            >
              <span className="rk-pc-mode-tile__title">
                {isPetFlow
                  ? "Pet design + name"
                  : isKidFlow
                    ? "Kid's drawing + name"
                    : "Portrait + name"}
              </span>
              <span className="rk-pc-mode-tile__desc">
                {isPetFlow
                  ? "Pet photo on top, their name stitched underneath."
                  : isKidFlow
                    ? "Their drawing on top, their name stitched underneath."
                    : "Portrait on top, their name stitched underneath."}
              </span>
            </button>
            <button
              type="button"
              className={`rk-pc-mode-tile${isTextOnly ? " is-selected" : ""}`}
              onClick={pickTextOnly}
            >
              <span className="rk-pc-mode-tile__title">Just text</span>
              <span className="rk-pc-mode-tile__desc">
                {`Stitch the ${designSubjectPossessive} name on a sleeve or the back.`}
              </span>
            </button>
          </div>
        </Substep>
      )}
      <Substep
        id="placement"
        label={flowCopy(config, "placementLabel", "Where on the garment?")}
        complete={confirmed.has("placement")}
        summary={activePlacement?.name ?? ""}
        openStep={openStep}
        onToggle={toggleStep}
      >
        <div className="rk-pc-pl-grid">
          {placements
            // Phase 67e: progressive disclosure. Placements flagged
            // `requiresAnyPriorSpot` are hidden from the picker until
            // the customer has saved at least one OTHER spot. The
            // customer-facing flow is: place the primary centre design
            // first, then click "Add another spot" → sleeves appear.
            // We never hide the customer's currently-active placement
            // (so re-editing an existing sleeve spot doesn't make it
            // disappear when they momentarily clear the saved list).
            .filter((p) => {
              if (!p.requiresAnyPriorSpot) return true;
              if (p.slug === draft.placementSlug) return true;
              return hasPriorSpots;
            })
            .map((p) => {
            const locked = lockedSlugs.get(p.slug);
            const isSelected = p.slug === draft.placementSlug;
            // Don't lock the currently-selected slug. The customer just
            // picked it on this draft; their own selection isn't a conflict.
            return (
              <PlacementCard
                key={p.id}
                placement={p}
                selected={isSelected}
                disabled={!!locked && !isSelected}
                disabledReason={locked}
                onClick={() => pickPlacement(p)}
              />
            );
          })}
        </div>
      </Substep>

      {/* Phase 67b: gate the Design/Text/Both picker on the active
        * placement's `allowsText` / `allowsLogo` flags. If the placement
        * is text-only (allowsLogo === false), we skip the picker entirely
        * and force mode=text. Same for logo-only. The picker only shows
        * when there's a real choice + the template is the standard logo
        * flow (pet + kid flows skip it). */}
      {(() => {
        const allowText = activePlacement?.allowsText !== false;
        const allowLogo = activePlacement?.allowsLogo !== false;
        // Phase 70: read flow kind from the FlowBlueprint instead of
        // the legacy template column.
        const isStandardFlow = flowKindFor(config) === "logo";

        // Auto-force the mode when only one option is allowed. Runs as
        // an effect so the draft is consistent before save validation.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        React.useEffect(() => {
          if (!activePlacement) return;
          // 2026-07-25: the artwork-forbidden coercion now runs on EVERY
          // flow. It used to be gated behind isStandardFlow, so on the
          // pet/kid/person flows a placement with allowsLogo:false kept
          // mode "logo" with the design upsell attached - the customer
          // could upload a photo to a spot the template says cannot take
          // artwork, and production got an impossible job. Safe on all
          // flows because "no artwork here" genuinely means the design
          // upsell does not apply.
          if (
            allowText &&
            !allowLogo &&
            !templateTextDisabled &&
            draft.mode !== "text"
          ) {
            set({ mode: "text", addonUpsellId: null, uploadLater: false });
          } else if (
            // The reverse coercion stays scoped to the standard logo flow:
            // on a single-design flow the addonUpsellId IS the digitisation
            // charge, so nulling it here would silently zero the fee.
            isStandardFlow &&
            !allowText &&
            allowLogo &&
            draft.mode !== "logo"
          ) {
            set({ mode: "logo", addonUpsellId: null });
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [activePlacement?.slug, allowText, allowLogo, templateTextDisabled]);

        if (!activePlacement || !isStandardFlow) return null;
        // Only one mode allowed → no picker, mode forced via the effect.
        if ((allowText && !allowLogo) || (!allowText && allowLogo)) return null;

        return (
        <Substep
          id="mode"
          label={flowCopy(config, "modeLabel", "What's the design?")}
          complete={!!draft.mode}
          summary={
            draft.addonUpsellId
              ? config.upsells?.find((u) => u.id === draft.addonUpsellId)?.label ?? ""
              : draft.mode === "logo"
                ? "Design"
                : draft.mode === "text"
                  ? "Text"
                  : draft.mode === "both"
                    ? "Design + text"
                    : ""
          }
          openStep={openStep}
          onToggle={toggleStep}
        >
          <div className="rk-pc-mode-row rk-pc-mode-row--three">
            {allowLogo && (
              <button
                type="button"
                className={`rk-pc-mode-tile${draft.mode === "logo" && !draft.addonUpsellId ? " is-selected" : ""}`}
                onClick={() => set({ mode: "logo", addonUpsellId: null })}
              >
                <span className="rk-pc-mode-tile__title">Design</span>
                <span className="rk-pc-mode-tile__desc">Upload your design.</span>
              </button>
            )}
            {allowText && (
              <button
                type="button"
                className={`rk-pc-mode-tile${draft.mode === "text" ? " is-selected" : ""}`}
                onClick={() =>
                  set({ mode: "text", addonUpsellId: null, uploadLater: false })
                }
              >
                <span className="rk-pc-mode-tile__title">Text</span>
                <span className="rk-pc-mode-tile__desc">Pick from {fonts.length} fonts.</span>
              </button>
            )}
            {allowText && allowLogo && (
              <button
                type="button"
                className={`rk-pc-mode-tile${draft.mode === "both" ? " is-selected" : ""}`}
                onClick={() => set({ mode: "both", addonUpsellId: null })}
              >
                <span className="rk-pc-mode-tile__title">Both</span>
                <span className="rk-pc-mode-tile__desc">Design + text together.</span>
              </button>
            )}
          </div>

          {/* Phase 17c: add-ons that replace the logo-upload step.
              Note: only visible on the standard logo template (gated
              by config.product.customiserFlow check above). Pet + kid
              templates use a dedicated flow + don't show these. */}
          {(() => {
            const logoReplacers = (config.upsells ?? []).filter(
              (u) =>
                u.appliesTo === "logo" &&
                u.replacesStep === "logo-upload" &&
                u.swapLabel &&
                // Pet-photo trap: the photo-upgrade upsell rides in
                // config.upsells so pricing can resolve it by id, but
                // it's offered ONLY via the detection card on the
                // upload step - never as a permanent tile here.
                u.id !== photoUpgrade?.id,
            );
            if (logoReplacers.length === 0) return null;
            return (
              <div className="rk-pc-addon-row">
                <span className="rk-pc-addon-row__head">Or instead</span>
                <div className="rk-pc-mode-row">
                  {logoReplacers.map((u) => {
                    const selected = draft.addonUpsellId === u.id;
                    const accent = u.accent ?? undefined;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        className={`rk-pc-mode-tile rk-pc-mode-tile--upsell${selected ? " is-selected" : ""}`}
                        style={accent ? ({ ["--rk-pc-upsell-accent" as any]: accent } as React.CSSProperties) : undefined}
                        onClick={() =>
                          // Phase 33b/c: prime addonCount + style defaults
                          // when the customer picks a pet/kid drawing tile
                          // so the stepper + toggle render right away.
                          set({
                            mode: "logo",
                            addonUpsellId: u.id,
                            addonCount: u.countMax && u.countMax > 1 ? 1 : null,
                            addonStyleColoured: false,
                          })
                        }
                      >
                        <span className="rk-pc-mode-tile__title">{u.label}</span>
                        <span className="rk-pc-mode-tile__desc">{u.hint ?? ""}</span>
                        <span className="rk-pc-mode-tile__price">
                          +${(u.priceCents / 100).toFixed(0)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </Substep>
        );
      })()}

      {/* Phase 91: standalone sleeve emoji picker. Decorative motifs the
          customer can add to a sleeve design (+$7 each, stacked via
          extraAddonIds -> pricing.ts charges each upsell's priceCents).
          Sleeve placements only. Optional add-on, so it sits outside the
          step accordion and stays reachable while the customer builds the
          sleeve spot. The spot still needs its normal name/logo to save. */}
      {activePlacement && isSleeveSlug(draft.placementSlug) && (() => {
        const emojiUpsells = (config.upsells ?? []).filter((u) =>
          isSleeveEmojiKind(u.kind),
        );
        if (emojiUpsells.length === 0) return null;
        const ticked = new Set(draft.extraAddonIds);
        const toggle = (id: string) => {
          const next = new Set(ticked);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          set({ extraAddonIds: Array.from(next) });
        };
        const count = ticked.size;
        // Sum the TICKED upsells' real prices - the hint used to hardcode
        // count * $7, which lies the moment any emoji's priceCents differs.
        const tickedCents = emojiUpsells.reduce(
          (sum, u) => (ticked.has(u.id) ? sum + u.priceCents : sum),
          0,
        );
        return (
          <div className="rk-pc-emoji-picker">
            <div className="rk-pc-sleeve-emojis">
              <span className="rk-pc-sleeve-emojis__label">
                Add a sleeve emoji (optional) - $7 each
              </span>
              <div className="rk-pc-sleeve-emojis__grid">
                {emojiUpsells.map((u) => {
                  const isOn = ticked.has(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className={`rk-pc-sleeve-emojis__chip${isOn ? " is-selected" : ""}`}
                      aria-pressed={isOn}
                      onClick={() => toggle(u.id)}
                    >
                      <span className="rk-pc-sleeve-emojis__glyph">
                        {u.appendGlyph ?? "✦"}
                      </span>
                      <span className="rk-pc-sleeve-emojis__name">{u.label}</span>
                      <span className="rk-pc-sleeve-emojis__price">
                        +${(u.priceCents / 100).toFixed(0)}
                      </span>
                    </button>
                  );
                })}
              </div>
              {count > 0 && (
                <span className="rk-pc-sleeve-emojis__hint">
                  {count} {count === 1 ? "emoji" : "emojis"} added &middot; +$
                  {(tickedCents / 100).toFixed(0)} at checkout
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Logo editor: visible for "logo" OR "both" modes. When an add-on
          with replacesStep="logo-upload" is selected, the substep label
          swaps to the upsell's swapLabel (e.g. "Upload your pet photo"). */}
      {activePlacement && (draft.mode === "logo" || draft.mode === "both") && (
        <Substep
          id="upload"
          label={(() => {
            if (draft.addonUpsellId) {
              const u = config.upsells?.find((x) => x.id === draft.addonUpsellId);
              if (u?.swapLabel) return u.swapLabel;
            }
            return flowCopy(config, "uploadLabel", "Upload your design");
          })()}
          complete={!!draft.uploadStagingKey || !!draft.uploadLater}
          summary={
            draft.uploadFilename ??
            (draft.uploadStagingKey
              ? "Design uploaded"
              : draft.uploadLater
                ? "Sending it later"
                : "")
          }
          openStep={openStep}
          onToggle={toggleStep}
        >
          {/* Reuse tiles for any previously-uploaded logos in this session.
              Picking one skips the +$30 digitisation. */}
          {priorLogos.length > 0 && (
            <div className="rk-pc-reuse-row">
              {priorLogos.map((pl) => (
                <LogoReuseTile
                  key={pl.stagingKey}
                  prior={pl}
                  onPick={() => {
                    set({
                      uploadStagingKey: pl.stagingKey,
                      uploadFilename: pl.filename,
                      uploadMimeType: pl.mimeType,
                      uploadFileHash: pl.fileHash,
                      // 2026-08-21 (back spot reusing the front design
                      // charged +$30): reusing a design already on this
                      // order is ONE digitisation event. Scan flows
                      // (kid/pet drawing) charge per spot unless the
                      // spot is marked already-digitised, so the tile
                      // must stamp it - and a reuse -> new design ->
                      // reuse round-trip must clear everything the
                      // abandoned new-design choice left behind.
                      uploadAlreadyDigitised: true,
                      sourceOrderName: pl.sourceOrderName,
                      reusedFromSpot: pl.fromSpotIndex,
                      uploadLater: false,
                      legacyUploadUrl: null,
                      uploadPhotoLike: false,
                      photoUpgradeDeclined: false,
                    });
                    // Wave 1 (tap tax): picking a reuse tile IS the
                    // design choice - same auto-confirm as a fresh
                    // upload when no add-on config UI needs a look-over.
                    if (!uploadAddonConfigVisible) confirmStep("upload");
                  }}
                />
              ))}
            </div>
          )}
          <LogoDropZone
            workerUrl={workerUrl}
            customerEmail={customerEmail}
            onBusyChange={setUploadBusy}
            stagingKey={draft.uploadStagingKey}
            filename={draft.uploadFilename}
            mimeType={draft.uploadMimeType}
            acceptedMimeTypes={config.settings?.acceptedMimeTypes ?? ["image/png", "image/jpeg", "image/webp", "image/svg+xml", "application/pdf"]}
            fileMaxMb={config.settings?.fileMaxMb ?? 10}
            onUploaded={async (res, file, uploadMs) => {
              // Pet-photo trap (2026-07-12): classify the fresh upload
              // BEFORE writing the draft so the photo-like flag lands in
              // the same set() as the upload fields (an async follow-up
              // set() would spread a stale draft closure). Runs ONLY on
              // the logo flow with a configured photoUpgrade and no scan
              // add-on already picked; isPhotoLike carries a hard 300ms
              // budget and resolves false on timeout/error, so worst
              // case the upload confirms 300ms later than before.
              let photoLike = false;
              if (photoUpgrade && !draft.addonUpsellId) {
                photoLike = await isPhotoLike(file);
              }
              set({
                uploadStagingKey: res.stagingKey,
                uploadFilename: file.name,
                uploadMimeType: res.contentType,
                uploadFileHash: res.fileHash ?? null,
                uploadAlreadyDigitised: !!res.alreadyDigitised,
                // A real upload supersedes "I'll send it later".
                uploadLater: false,
                // Clear the legacy ref now that there's a fresh upload.
                legacyUploadUrl: null,
                // Phase 67i: when the (email, hash) lookup matched a past
                // order, stamp that order on the draft. propertiesFor
                // emits a visible "Spot N reused from <order>" so
                // production knows we recognised + waived the fee.
                sourceOrderName: res.firstOrderName ?? null,
                // A fresh upload is NOT a same-session reuse any more -
                // clear the tile marker so the fee + provenance follow
                // the new artwork (reuse -> new design round-trip).
                reusedFromSpot: null,
                uploadPhotoLike: photoLike,
              });
              if (photoLike && !photoDetectedFiredRef.current) {
                photoDetectedFiredRef.current = true;
                emitAnalytics("photo_detected", {
                  placementSlug: draft.placementSlug,
                  mode: draft.mode,
                  flowKind: flow,
                });
              }
              // Wave 1 funnel: first successful upload for this spot.
              if (!uploadDoneFiredRef.current) {
                uploadDoneFiredRef.current = true;
                emitAnalytics("upload_done", {
                  placementSlug: draft.placementSlug,
                  mode: draft.mode,
                  flowKind: flow,
                  uploadMs,
                });
              }
              // Wave 1 (tap tax): with no add-on config UI under the
              // dropzone, a successful upload IS the confirmation - the
              // step collapses and the flow advances without the old
              // "Confirm design" tap. Re-uploads on an existing spot
              // just re-run the (idempotent) confirm.
              //
              // Pet-photo trap: when the Portrait upgrade card is about
              // to show, the auto-advance must NOT fire - the step stays
              // open on the choice. Either card button (or collapsing
              // the step by hand) moves the flow on.
              const photoOfferWillShow =
                photoLike && !!photoUpgrade && !draft.photoUpgradeDeclined;
              if (!uploadAddonConfigVisible && !photoOfferWillShow) {
                confirmStep("upload");
              }
            }}
          />
          {/* Upload-later (2026-07-17): "I'll send it later" escape hatch
              under the dropzone. Lets a customer buy now and email/portal
              the artwork after checkout - the order holds fulfilment until
              it lands. Hidden once a real file is staged (the upload wins);
              tapping it stands in for the upload and advances the flow. */}
          {!draft.uploadStagingKey && !draft.uploadLater && (
            <button
              type="button"
              className="rk-pc-upload-later"
              onClick={() => {
                set({ uploadLater: true });
                emitAnalytics("upload_later_chosen", {
                  placementSlug: draft.placementSlug,
                  mode: draft.mode,
                  flowKind: flow,
                });
                confirmStep("upload");
              }}
            >
              Don't have it handy? <strong>I'll send it later</strong> - order
              now, upload the {flow === "kid" ? "drawing" : "design"} when
              it's ready
            </button>
          )}
          {!draft.uploadStagingKey && draft.uploadLater && (
            <div className="rk-pc-upload-later-chip">
              <span className="rk-pc-upload-later-chip__tick">✓</span>
              <span className="rk-pc-upload-later-chip__copy">
                Sending it later - after checkout we email you a link to
                upload it. Your 2-week turnaround starts once we have it.
              </span>
              <button
                type="button"
                className="rk-pc-upload-later-chip__undo"
                onClick={() => set({ uploadLater: false })}
              >
                Upload now instead
              </button>
            </div>
          )}
          {/* Pet-photo trap (2026-07-12): the in-place "Upgrade to
              Portrait" offer. Renders only when the classifier judged
              this upload photo-like, the blueprint wired a photoUpgrade
              upsell, the spot hasn't already taken it, and the customer
              hasn't declined it on this spot. Sits ABOVE the fee note +
              confirm point; collapses on either choice. Dismissal is
              not required to proceed - collapsing the step by hand
              still confirms it. */}
          {(() => {
            if (
              !photoUpgrade ||
              !draft.uploadStagingKey ||
              !draft.uploadPhotoLike ||
              draft.photoUpgradeDeclined ||
              draft.addonUpsellId === photoUpgrade.id
            ) {
              return null;
            }
            const baseCents = effectiveUpsellPriceCents(photoUpgrade, config);
            const styleCents = effectiveUpsellStyleSurchargeCents(
              photoUpgrade,
              config,
            );
            const accent = photoUpgrade.accent ?? undefined;
            return (
              <div className="rk-pc-addon-row rk-pc-photo-upgrade">
                <span className="rk-pc-addon-row__head">
                  That looks like a photo!
                </span>
                <p className="rk-pc-threads__hint">
                  Photos stitch best as a Portrait: our artists redraw it,
                  then we stitch it coloured + filled. Simple logos stitch
                  as bold shapes in a few thread colours - photo detail
                  can't be stitched as-is.
                </p>
                <div className="rk-pc-mode-row">
                  <button
                    type="button"
                    className="rk-pc-mode-tile rk-pc-mode-tile--upsell"
                    style={
                      accent
                        ? ({
                            ["--rk-pc-upsell-accent" as any]: accent,
                          } as React.CSSProperties)
                        : undefined
                    }
                    onClick={acceptPhotoUpgrade}
                  >
                    <span className="rk-pc-mode-tile__title">
                      Upgrade to Portrait (+${(baseCents / 100).toFixed(0)})
                    </span>
                    <span className="rk-pc-mode-tile__desc">
                      {styleCents > 0
                        ? `An artist redraws your photo. Coloured + filled (+$${(styleCents / 100).toFixed(0)}).`
                        : "An artist redraws your photo, stitched coloured + filled."}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="rk-pc-mode-tile"
                    onClick={declinePhotoUpgrade}
                  >
                    <span className="rk-pc-mode-tile__title">
                      Keep as a simple logo
                    </span>
                    <span className="rk-pc-mode-tile__desc">
                      Bold shapes in a few thread colours.
                    </span>
                  </button>
                </div>
              </div>
            );
          })()}
          {/* Phase 33b/c: when the customer picked a count- or style-
              enabled upsell (pet portrait, kid's drawing), expose the
              "how many pets?" stepper + the line/coloured style toggle
              right under the upload step. Each updates the draft so
              pricing.ts can stack the surcharges. */}
          {(() => {
            const u = draft.addonUpsellId
              ? config.upsells?.find((x) => x.id === draft.addonUpsellId)
              : null;
            if (!u) return null;
            const supportsCount = u.countMax && u.countMax > 1;
            // Phase 67c: template-level lockedAddonStyle suppresses the
            // line/coloured toggle entirely. The locked style is applied
            // to the draft on mount + the surcharge is skipped server-
            // side. Used by rk-pet-varsity where coloured+filled is
            // included in the garment price, no choice for the customer.
            // Phase 67h: a bypass placement (pet-varsity back) ignores the
            // template lock and re-exposes the style toggle.
            const lockedStyle = lockedAddonStyle;
            const supportsStyle = !!u.styleSurchargeCents && !lockedStyle;
            if (!supportsCount && !supportsStyle) return null;
            // Phase 39c: drive count UI from extraUploads on pet flow.
            // Each additional pet photo = +$5 (the upsell's perCountCents).
            // We cap at countMax - 1 extras since the primary slot fills
            // the first.
            const extras = draft.extraUploads ?? [];
            const totalCount = 1 + extras.length;
            const canAddMore = !!u.countMax && totalCount < u.countMax;
            // Pet + person flows use multi-image upload (a family
            // portrait or pack of pets = multiple source photos). Other
            // count-enabled upsells fall back to the stepper.
            const useMultiUpload = (isPetFlow || isPersonFlow) && supportsCount;
            return (
              <div className="rk-pc-addon-config">
                {useMultiUpload && (
                  <div className="rk-pc-addon-config__field">
                    <span className="rk-pc-addon-config__label">
                      {totalCount === 1
                        ? `Adding another ${extraSubjectSingular} to the design?`
                        : `${totalCount} ${totalCount === 1 ? extraSubjectSingular : extraSubjectPlural} in this design`}
                    </span>
                    {extras.length > 0 && (
                      <div className="rk-pc-extra-uploads">
                        {extras.map((eu, idx) => (
                          <div
                            key={`${eu.stagingKey}-${idx}`}
                            className="rk-pc-extra-uploads__row"
                          >
                            <span className="rk-pc-extra-uploads__name">
                              {`#${idx + 2}  ${eu.filename || "uploaded photo"}`}
                            </span>
                            <button
                              type="button"
                              className="rk-pc-extra-uploads__remove"
                              onClick={() => {
                                const next = extras.filter((_, i) => i !== idx);
                                set({ extraUploads: next });
                              }}
                              aria-label="Remove this photo"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {canAddMore && (
                      <ExtraUploadAdder
                        workerUrl={workerUrl}
                        customerEmail={customerEmail}
                        subject={extraSubjectSingular}
                        acceptedMimeTypes={config.settings?.acceptedMimeTypes ?? ["image/png", "image/jpeg", "image/webp", "image/svg+xml", "application/pdf"]}
                        fileMaxMb={config.settings?.fileMaxMb ?? 10}
                        onUploaded={(res, file) => {
                          set({
                            extraUploads: [
                              ...extras,
                              {
                                stagingKey: res.stagingKey,
                                filename: file.name,
                                mimeType: res.contentType,
                                fileHash: res.fileHash ?? null,
                              },
                            ],
                          });
                        }}
                      />
                    )}
                    <span className="rk-pc-addon-config__hint">
                      {totalCount > 1
                        ? `+$${(((totalCount - 1) * (u.perCountCents ?? 0)) / 100).toFixed(0)} for ${totalCount - 1} extra ${totalCount - 1 === 1 ? extraSubjectSingular : extraSubjectPlural}`
                        : `Each extra ${extraSubjectSingular} photo is +$${((u.perCountCents ?? 0) / 100).toFixed(0)}. Up to ${u.countMax} ${extraSubjectPlural} per design.`}
                    </span>
                  </div>
                )}
                {!useMultiUpload && supportsCount && (
                  <div className="rk-pc-addon-config__field">
                    <span className="rk-pc-addon-config__label">
                      How many subjects in this design?
                    </span>
                    <div className="rk-pc-addon-config__stepper">
                      {Array.from({ length: u.countMax! }, (_, i) => i + 1).map(
                        (n) => (
                          <button
                            key={n}
                            type="button"
                            className={`rk-pc-addon-config__step${(draft.addonCount ?? 1) === n ? " is-selected" : ""}`}
                            onClick={() => set({ addonCount: n })}
                          >
                            {n}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                )}
                {supportsStyle && (
                  <div className="rk-pc-addon-config__field">
                    <span className="rk-pc-addon-config__label">
                      Drawing Style
                    </span>
                    <div className="rk-pc-mode-row">
                      <button
                        type="button"
                        className={`rk-pc-mode-tile${!draft.addonStyleColoured && !draft.addonStyleSingleLine ? " is-selected" : ""}`}
                        onClick={() =>
                          set({
                            addonStyleColoured: false,
                            addonStyleSingleLine: false,
                          })
                        }
                      >
                        <span className="rk-pc-mode-tile__title">Line drawing</span>
                        <span className="rk-pc-mode-tile__desc">
                          A line-drawing done by an artist
                        </span>
                        <span className="rk-pc-mode-tile__price">Included</span>
                      </button>
                      {/* 2026-07-19 (Oscar): stylised single-line style,
                        * person flow only. One continuous stroke, free. */}
                      {isPersonFlow && (
                        <button
                          type="button"
                          className={`rk-pc-mode-tile${draft.addonStyleSingleLine ? " is-selected" : ""}`}
                          onClick={() =>
                            set({
                              addonStyleSingleLine: true,
                              addonStyleColoured: false,
                            })
                          }
                        >
                          <span className="rk-pc-mode-tile__title">
                            Single line
                          </span>
                          <span className="rk-pc-mode-tile__desc">
                            Drawn in one continuous stroke, minimal and
                            artistic
                          </span>
                          <span className="rk-pc-mode-tile__price">
                            Included
                          </span>
                        </button>
                      )}
                      <button
                        type="button"
                        className={`rk-pc-mode-tile${draft.addonStyleColoured ? " is-selected" : ""}`}
                        onClick={() =>
                          set({
                            addonStyleColoured: true,
                            addonStyleSingleLine: false,
                          })
                        }
                      >
                        <span className="rk-pc-mode-tile__title">
                          Coloured + filled in
                        </span>
                        <span className="rk-pc-mode-tile__desc">
                          Full fill stitch in the colour of the design uploaded
                        </span>
                        <span className="rk-pc-mode-tile__price">
                          +${((u.styleSurchargeCents ?? 0) / 100).toFixed(0)}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
                {/* 2026-07-19 (Oscar): people drawings are faceless by
                  * default; the customer can opt into faces with an
                  * honest warning about small placements + group shots.
                  * Person flow only - pet portraits keep faces as-is. */}
                {isPersonFlow && (
                  <div className="rk-pc-addon-config__field">
                    <span className="rk-pc-addon-config__label">Faces</span>
                    <div className="rk-pc-mode-row">
                      <button
                        type="button"
                        className={`rk-pc-mode-tile${!draft.includeFaces ? " is-selected" : ""}`}
                        onClick={() => set({ includeFaces: false })}
                      >
                        <span className="rk-pc-mode-tile__title">No faces</span>
                        <span className="rk-pc-mode-tile__desc">
                          The classic silhouette look most people pick
                        </span>
                        <span className="rk-pc-mode-tile__price">Included</span>
                      </button>
                      <button
                        type="button"
                        className={`rk-pc-mode-tile${draft.includeFaces ? " is-selected" : ""}`}
                        onClick={() => set({ includeFaces: true })}
                      >
                        <span className="rk-pc-mode-tile__title">
                          Include faces
                        </span>
                        <span className="rk-pc-mode-tile__desc">
                          Simple stitched features
                        </span>
                        <span className="rk-pc-mode-tile__price">Included</span>
                      </button>
                    </div>
                    {draft.includeFaces && (
                      <span className="rk-pc-addon-config__note">
                        Heads up: faces stitch as a few simple lines. With
                        several people in the design, or on smaller
                        placements, they can crowd together and lose the
                        likeness. Happy to stitch them, but if you want the
                        safest look, go without.
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          {/* Audit 2026-10: the note also shows for "I'll send it later"
              on a logo-flow spot that already has a prior design, because
              pricing.digitisationEventsFor charges that spot the fee (it
              is a real, new design arriving after checkout). Scan flows
              (addonUpsellId set) price the scan instead, so they keep the
              upload-only gate. */}
          {(draft.uploadStagingKey ||
            (draft.uploadLater &&
              !draft.addonUpsellId &&
              priorLogos.length > 0)) && (
            <span className="rk-pc-fee-note">
              {draft.uploadStagingKey &&
              priorLogos.some((p) => p.stagingKey === draft.uploadStagingKey) ? (
                <>Already digitised in this design, no extra fee.</>
              ) : draft.uploadAlreadyDigitised ? (
                <>This design's already been digitised, no extra fee.</>
              ) : config.settings?.digitisationFreePromo ? (
                /* Phase 68: free-digitisation promo. Show the original
                   $30 crossed out next to FREE so the customer reads
                   the saving as part of the offer, not a hidden gift. */
                <>
                  <span className="rk-pc-fee-note__was">
                    +${((config.product.digitizationFeeCents ?? 3000) / 100).toFixed(0)}
                  </span>{" "}
                  <strong className="rk-pc-fee-note__free">FREE</strong> digitisation
                  fee for a limited time (so your design stitches cleanly).
                </>
              ) : priorLogos.length === 0 ? (
                /* Phase 85: first design's digitising is included (baked
                   into the garment price). No prior design uploaded on
                   this garment => this is the free first placement. */
                <>
                  <strong className="rk-pc-fee-note__free">Free</strong>{" "}
                  design digitising on your first placement (so it stitches
                  cleanly).
                </>
              ) : (
                <>
                  <strong>
                    +$
                    {((config.product.digitizationFeeCents ?? 3000) / 100).toFixed(0)}
                  </strong>{" "}
                  one-time digitising fee for this extra design (so it
                  stitches cleanly).
                  {!draft.uploadStagingKey && draft.uploadLater
                    ? " Charged now, digitised once the design arrives."
                    : null}
                </>
              )}
            </span>
          )}
          {/* 2026-07-03: the "Remove the background" checkbox moved from
              here onto the LIVE PREVIEW dock (PreviewCanvas) - it is a
              visual-only control and its caveat paragraph read as friction
              in the upload step. */}
          {/* Wave 1 (tap tax): the explicit Confirm only remains when
              add-on config UI (photo adder / stepper / style toggle)
              renders above - otherwise the upload itself confirms. */}
          {uploadAddonConfigVisible && (
            <StepConfirm
              onConfirm={() => confirmStep("upload")}
              disabled={!draft.uploadStagingKey}
              label="Confirm design"
            />
          )}
        </Substep>
      )}

      {/* Text editor: visible for "text" OR "both" modes, plus on pet
          and kid templates once a photo has been uploaded so the
          customer can stitch the pet's / kid's name with the drawing.
          NOT shown when the customer is on the 3-tile picker and chose
          "Pet design" (design only, no text) — that's an explicit opt-out.
          Phase 67d: when the template has textRequired=true, the
          label says "Stitch their name <arrangement> the design" and
          the save-spot gate refuses empty text. */}
      {activePlacement &&
        (draft.mode === "text" ||
          draft.mode === "both" ||
          (isSingleDesignFlow &&
            // Text-disabled template: no optional name under the design.
            // Kept OUT of the text/both disjuncts above - the sleeve-name
            // upsell's mode:"text" draft has no other text/font editor.
            !templateTextDisabled &&
            !!draft.uploadStagingKey &&
            !showPetOrTextPicker)) && (
        <Substep
          id="text"
          label={(() => {
            // Phase 67b: date-input mode override. The text field is a
            // date picker, so the label needs to say so up front.
            // Phase 67g: but only for the PRIMARY (chest) spot — sleeves
            // on the same template ask for initials.
            if (
              config.product.dateInputRoman &&
              !activePlacement?.requiresAnyPriorSpot
            ) {
              return "Pick the date - we'll stitch it in Roman numerals";
            }
            if (
              config.product.dateInputRoman &&
              activePlacement?.requiresAnyPriorSpot
            ) {
              return "Stitch initials on the sleeve";
            }
            if (isSingleDesignFlow) {
              const required = textRequired;
              const where = lockedArrangement;
              if (required && where) {
                return `Stitch their name ${where} the design`;
              }
              if (required) {
                return `Stitch ${designSubjectPossessive} name on the design`;
              }
              // Wave 1 (tap tax): blank optional text auto-confirms, so
              // this header is the collapsed step's whole pitch - short
              // and clearly optional.
              return isPersonFlow
                ? "Add their name (optional)"
                : `Add your ${designSubjectPossessive} name (optional)`;
            }
            return flowCopy(
              config,
              "textLabel",
              "What would you like to embroider?",
            );
          })()}
          complete={confirmed.has("text")}
          summary={draft.text}
          openStep={openStep}
          onToggle={toggleStep}
        >
          {(() => {
            // Phase 67b: date-input mode. When the template carries
            // `dateInputRoman`, swap the text field for a native HTML
            // date picker. We keep an internal ISO string in a
            // sessionStorage-ish ref via a separate substep state, and
            // mirror the converted Roman numeral string into `draft.text`
            // so the rest of the pipeline (validation, cart properties,
            // stitch instructions) sees it as a normal text spot.
            //
            // Phase 67g: SECONDARY spots on a date template (sleeves on
            // rk-special-date) are gated behind `requiresAnyPriorSpot`.
            // Those want a plain text input for initials — NOT the date
            // picker. So skip the swap when the active placement carries
            // that flag.
            if (
              config.product.dateInputRoman &&
              !activePlacement?.requiresAnyPriorSpot
            ) {
              // Extract the ISO date we last saved if the current text
              // matches a known conversion; otherwise blank. The cart
              // line stores ONLY the Roman string, so on re-edit we
              // can't recover the originating date — that's OK,
              // customer just picks again.
              return (
                <div className="rk-pc-date-picker">
                  <input
                    type="date"
                    className="rk-pc-text-input"
                    // Min/max: lifelong range, prevents nonsense years.
                    min="1900-01-01"
                    max="2100-12-31"
                    onChange={(e) => {
                      const iso = e.target.value;
                      const roman = dateIsoToRoman(iso);
                      // Mirror: text = roman string. If they clear the
                      // date, blank text so save-validation fails until
                      // they pick a new one.
                      set({ text: roman });
                    }}
                  />
                  {draft.text && (
                    <div className="rk-pc-date-preview" aria-live="polite">
                      <span className="rk-pc-date-preview__label">Will be stitched as</span>
                      <strong>{draft.text}</strong>
                    </div>
                  )}
                </div>
              );
            }

            // Phase 32b: when the picked font has no lowercase glyphs
            // (College, Stencil Block), upper-case the input on the fly
            // so the customer can't type something that the stitched
            // output will silently rewrite. The transformation also
            // applies to mid-text edits (selection + typing).
            const activeFont = draft.fontId
              ? fonts.find((f) => f.id === draft.fontId)
              : null;
            const forceUpper = !!activeFont?.forceUppercase || wordArtMode;
            // Phase 67d: when a font is locked (template-level), apply
            // its CSS font-family + letter-spacing as inline style so
            // the customer sees the stitched look as they type. Picker
            // is hidden by lockedFontId so without this they'd only
            // see the input in the default body font — wrong feedback.
            const inputStyle: React.CSSProperties = {};
            if (forceUpper) inputStyle.textTransform = "uppercase";
            if (activeFont) {
              inputStyle.fontFamily = `'${activeFont.family}', system-ui, sans-serif`;
              if (activeFont.tracking) {
                inputStyle.letterSpacing = activeFont.tracking;
              }
              // A slight bump in size so the font character is readable
              // in the input — pairing CSS uses 15px which is too small
              // for display faces like College.
              inputStyle.fontSize = "20px";
              inputStyle.lineHeight = "1.2";
            }
            // Word Art (2026-07): show the input in the chosen preset's
            // display face (caps-only) so typing previews the stitched look.
            if (wordArtMode) {
              const wp =
                (draft.wordArtStyleId &&
                  wordArtStyles.find((p) => p.id === draft.wordArtStyleId)) ||
                wordArtStyles[0];
              inputStyle.textTransform = "uppercase";
              inputStyle.fontFamily = wp.fontFamily;
              inputStyle.fontSize = "20px";
              inputStyle.lineHeight = "1.2";
            }
            // Phase 67d: arrangement to lock the draft to when text is
            // typed on a single-design flow. Defaults to "below" (legacy
            // behaviour); templates that locked the arrangement use that.
            // Phase 67h: bypass placements (e.g. pet-varsity back) fall
            // back to "below" since they ignore the template lock.
            const lockedArr =
              (lockedArrangement as
                | "above"
                | "below"
                | "left"
                | "right"
                | null
                | undefined) ?? "below";
            // Sleeve/arm slugs stay single-line (small embroidery area,
            // multi-line doesn't fit). Every other placement allows up to
            // MAX_LINES rows so the customer can stack name + date etc.
            // The template's maxLines was being ignored: MAX_LINES was
            // hardcoded to 3, so a template that says one line still offered
            // three and the placeholder still advertised them. Small panels
            // (cap back, sock cuff) physically cannot take three lines.
            const MAX_LINES = Math.max(1, config.product.maxLines || 3);
            const singleLineOnly =
              MAX_LINES === 1 ||
              (!!activePlacement && /sleeve|arm/i.test(activePlacement.slug));
            const perLineMax = config.product.maxCharsPerLine || 30;
            const placeholder = isSingleDesignFlow
              ? textRequired
                ? "Type their name…"
                : "Leave blank for design only, or type a name…"
              : flowCopy(
                  config,
                  "textPlaceholder",
                  singleLineOnly
                    ? "Type the text you want embroidered…"
                    : `Type the text you want embroidered (Enter for a new line, up to ${MAX_LINES} lines)…`,
                );
            // Normalise newlines, hard-cap to MAX_LINES lines, clip each
            // line to the per-line char budget (single-line spots strip
            // newlines entirely), then decide the mode. The rule lives in
            // ../textDraft.ts: single-design DESIGN drafts promote between
            // "logo" and "both" as text comes and goes; a text-only draft
            // (mode "text") just takes the text and never demands an
            // upload (audit 2026-10).
            const applyTextChange = (raw: string) => {
              set(
                textChangePatch(draft, raw, {
                  singleLineOnly,
                  maxLines: MAX_LINES,
                  perLineMax,
                  forceUpper,
                  isSingleDesignFlow,
                  textRequired,
                  lockedArrangement: lockedArr,
                }),
              );
            };
            // The cap back is always a year. Four digit boxes behind a fixed
            // EST. beats a 3-line textarea that let someone type a paragraph
            // into a 50mm panel.
            if (activePlacement && activePlacement.slug === "hat-back") {
              return (
                <YearBoxes
                  value={draft.text}
                  onChange={(next) => applyTextChange(next)}
                  onComplete={() => confirmStep("text")}
                />
              );
            }
            // The sock cuff is BEST DAD over EST. <year>. Both words are fixed
            // art, so the customer only ever types the four digits, and the
            // control shows exactly what will be stitched.
            //
            // 2026-08-03: gated on the TEMPLATE, not the slug alone. The plain
            // CUSTOM Socks product (tag RK_REGULAR) shares the outside-of-sock
            // slug but is a free-text spot - it was wrongly forcing BEST DAD /
            // EST. on it. Only the Best Dad socks and the gift box's sock stage
            // get the locked year.
            if (
              activePlacement &&
              activePlacement.slug === "outside-of-sock" &&
              isBestDadSockTemplate(config.templateTag)
            ) {
              return (
                <YearBoxes
                  aboveLabel="BEST DAD"
                  value={draft.text}
                  onChange={(next) => applyTextChange(next)}
                  onComplete={() => confirmStep("text")}
                />
              );
            }
            if (singleLineOnly) {
              return (
                <input
                  type="text"
                  className="rk-pc-text-input"
                  placeholder={placeholder}
                  value={draft.text}
                  maxLength={perLineMax}
                  style={
                    Object.keys(inputStyle).length > 0 ? inputStyle : undefined
                  }
                  onChange={(e) => applyTextChange(e.target.value)}
                />
              );
            }
            return (
              <textarea
                className="rk-pc-text-input rk-pc-text-input--multiline"
                placeholder={placeholder}
                value={draft.text}
                rows={MAX_LINES}
                maxLength={perLineMax * MAX_LINES + (MAX_LINES - 1)}
                style={
                  Object.keys(inputStyle).length > 0 ? inputStyle : undefined
                }
                onKeyDown={(e) => {
                  // Block Enter that would push past MAX_LINES — but also
                  // ignore composition events (IME) and let Shift+Enter
                  // behave the same as Enter (a newline).
                  if (e.key === "Enter") {
                    const lines = draft.text.split("\n").length;
                    if (lines >= MAX_LINES) {
                      e.preventDefault();
                    }
                  }
                }}
                onChange={(e) => applyTextChange(e.target.value)}
              />
            );
          })()}
          {/* Word Art (2026-07): the style gallery REPLACES the font picker
            * on a Word Art template - the preset is the face + effect.
            * Selecting a style clears any fontId and upper-cases the text
            * (every launch preset is caps-only). */}
          {wordArtMode && (
            <div className="rk-pc-font-grid rk-pc-wa-grid">
              {wordArtStyles.map((p) => (
                <WordArtStyleTile
                  key={p.id}
                  preset={p}
                  text={draft.text}
                  selected={p.id === draft.wordArtStyleId}
                  onClick={() =>
                    set({
                      wordArtStyleId: p.id,
                      fontId: null,
                      text: draft.text.toUpperCase(),
                    })
                  }
                />
              ))}
            </div>
          )}
          {/* Phase 67: template-level font lock. When config.product.lockedFontId
            * is set, hide the picker entirely and keep the locked font as the
            * draft's fontId. Filters the fonts grid to that single font so
            * the customer can't pick a different one even via DOM manipulation. */}
          {!wordArtMode && fonts.length > 0 && !lockedFontId && (
            <div className="rk-pc-font-grid">
              {fonts.map((f) => (
                <FontTile
                  key={f.id}
                  font={f}
                  text={draft.text}
                  selected={f.id === draft.fontId}
                  onClick={() => {
                    // Switching to an uppercase-only font upper-cases the
                    // existing text so the input doesn't silently change
                    // shape on the next keystroke.
                    if (f.forceUppercase && draft.text !== draft.text.toUpperCase()) {
                      set({ fontId: f.id, text: draft.text.toUpperCase() });
                    } else {
                      set({ fontId: f.id });
                    }
                  }}
                />
              ))}
            </div>
          )}
          <StepConfirm
            onConfirm={() => confirmStep("text")}
            disabled={!textComplete}
            label="Confirm text"
          />
        </Substep>
      )}

      {/* Phase B2: size sits directly under the text / design step. The
          slider has no natural "done" event, so a Confirm button locks it
          in and moves the flow on. */}
      {activePlacement && draft.mode && !lockedSizeMm && (
        <Substep
          id="size"
          label={flowCopy(
            config,
            "sizeLabel",
            "How big would you like the embroidery to be?",
          )}
          complete={confirmed.has("size")}
          summary={`${draft.sizeMm}mm`}
          openStep={openStep}
          onToggle={toggleStep}
        >
          <SizeSlider
            placement={activePlacement}
            sizeMm={draft.sizeMm}
            config={config}
            onChange={(n) => set({ sizeMm: n })}
          />
          <StepConfirm
            onConfirm={() => confirmStep("size")}
            label="Confirm size"
          />
        </Substep>
      )}

      {/* Arrangement picker: only when both are configured AND we're not
          on a single-design flow (pet/kid) AND the template hasn't
          locked the arrangement. Phase 67: lockedArrangement on the
          template forces every spot's layout to the same value (e.g.
          pet-varsity = "above" everywhere). */}
      {activePlacement &&
        draft.mode === "both" &&
        !isSingleDesignFlow &&
        !lockedArrangement && (
        <Substep
          id="arrangement"
          label="Where does the text sit?"
          complete={confirmed.has("arrangement")}
          summary={
            draft.arrangement === "above"
              ? "Text above"
              : draft.arrangement === "below"
                ? "Text below"
                : draft.arrangement === "left"
                  ? "Text left"
                  : draft.arrangement === "right"
                    ? "Text right"
                    : ""
          }
          openStep={openStep}
          onToggle={toggleStep}
        >
          <ArrangementPicker
            value={draft.arrangement}
            onChange={(arr) => {
              // Single-select: picking a layout IS confirming it.
              set({ arrangement: arr });
              confirmStep("arrangement");
            }}
          />
        </Substep>
      )}

      {/* Thread colours.
        *
        * Pet + kid flow (Phase 42a + 43a): ONE picker, single colour.
        * The drawing + any text under it stitch in the same single
        * thread, so we sync `logoThreadCodes` + `textThreadCodes` to
        * one value picked here.
        *
        * Standard / logo flow: text picks 1 colour, design picks up to
        * 3, "both" mode gets two pickers (one per portion).
        *
        * All pickers gate on the content prereqs so customers don't
        * pick threads before there's anything to colour. */}
      {activePlacement && isSingleDesignFlow && (draft.mode === "logo" || draft.mode === "both") && (draft.uploadStagingKey || draft.uploadLater) && (
        <Substep
          id="threads"
          label={flowCopy(config, "threadLabel", "Pick your thread colour")}
          complete={draft.logoThreadCodes.length > 0}
          summary={threadSummary(draft.logoThreadCodes)}
          openStep={openStep}
          onToggle={toggleStep}
        >
          <ThreadPicker
            threads={config.threads}
            value={draft.logoThreadCodes}
            onChange={(codes) => {
              // Sync both: the embroidery team gets the same code on
              // either field, no matter which portion they read. Pass
              // the WHOLE array through the slice so MATCH_DESIGN_CODE
              // (a 1-element array) and a single Isacord code (also
              // 1-element) both round-trip correctly.
              const single = codes.slice(0, 1);
              pickThread(
                "threads",
                () => set({ logoThreadCodes: single, textThreadCodes: single }),
                single.length > 0,
              );
            }}
            max={1}
            // Phase 79: "Match the design" tile only renders when the
            // customer picked "Coloured + filled in". On line drawings
            // the design IS a single colour - asking the customer to
            // colour-match is nonsense. See the sync useEffect above.
            matchOption={draft.addonStyleColoured}
            // Normally undefined here (the style toggle covers the fee on
            // single-design flows); wired anyway so any state where the
            // pricing dedupe does NOT cover it (e.g. a dangling add-on id)
            // shows its price instead of charging silently.
            matchPriceCents={matchPriceCents}
            heading="Thread"
            hint={
              draft.addonStyleColoured
                ? isPetFlow
                  ? "Coloured + filled portraits stitch the artwork's actual colours. We'll colour-match the threads when we digitise. Tap a swatch below if you'd rather stitch the whole thing in one colour instead."
                  : isPersonFlow
                    ? "Coloured + filled portraits stitch the artwork's actual colours. We'll colour-match the threads when we digitise. Tap a swatch below if you'd rather stitch the whole thing in one colour instead."
                    : "Coloured + filled drawings stitch the artwork's actual colours. We'll colour-match the threads when we digitise. Tap a swatch below if you'd rather stitch the whole thing in one colour instead."
                : isPetFlow
                  ? "Pet portraits stitch in one solid colour. The pet's name (if you added one) uses this same thread."
                  : isPersonFlow
                    ? "Line drawings stitch in one solid colour. Any text below uses this same thread."
                    : "Kid's drawings stitch in one solid colour. Any text under the drawing uses this same thread."
            }
          />
          {/* Wave 1 (tap tax): no Confirm row - the picker is single-
              select, so the swatch tap itself confirms (pickThread). */}
        </Substep>
      )}
      {activePlacement && !isSingleDesignFlow && draft.mode === "logo" && draft.uploadStagingKey && (
        <Substep
          id="threads"
          label="Pick the design's thread colour"
          complete={confirmed.has("threads")}
          summary={threadSummary(draft.logoThreadCodes)}
          openStep={openStep}
          onToggle={toggleStep}
        >
          <ThreadPicker
            threads={config.threads}
            value={draft.logoThreadCodes}
            onChange={(codes) =>
              pickThread(
                "threads",
                () => set({ logoThreadCodes: codes }),
                codes.length > 0,
              )
            }
            max={1}
            matchOption={designMatchAllowed}
            matchHintExtra={photoMatchHintExtra}
            matchPriceCents={matchPriceCents}
            heading="Design thread"
          />
          {/* Wave 1 (tap tax): no Confirm row - the picker is single-
              select, so the swatch tap itself confirms (pickThread). */}
        </Substep>
      )}
      {/* `lockedFontId` is accepted here alongside draft.fontId. On a
          locked-font template the picker never renders, so draft.fontId can
          be a frame behind the seeding effect above (or null outright if the
          draft was recreated). Without this the substep never mounts, the
          save gate sticks on "No thread colour chosen", and the spot can
          never be added to cart. Purely additive - it cannot hide a step
          that renders today. */}
      {activePlacement && draft.mode === "text" && draft.text.trim() && (draft.fontId || draft.wordArtStyleId || lockedFontId) && (
        <Substep
          id="threads"
          label="Pick the text's thread colour"
          complete={confirmed.has("threads")}
          summary={threadSummary(draft.textThreadCodes)}
          openStep={openStep}
          onToggle={toggleStep}
        >
          <ThreadPicker
            threads={config.threads}
            value={draft.textThreadCodes}
            onChange={(codes) => {
              if (isSingleDesignFlow) {
                // Pet/kid flow text-only spots: same single-colour rule.
                const single = codes.slice(0, 1);
                pickThread(
                  "threads",
                  () =>
                    set({ logoThreadCodes: single, textThreadCodes: single }),
                  single.length > 0,
                );
              } else {
                pickThread(
                  "threads",
                  () => set({ textThreadCodes: codes }),
                  codes.length > 0,
                );
              }
            }}
            max={1}
            heading={isSingleDesignFlow ? "Thread" : "Text thread"}
          />
          {/* Wave 1 (tap tax): no Confirm row - single-select picker,
              the swatch tap itself confirms (pickThread). */}
        </Substep>
      )}
      {activePlacement && !isSingleDesignFlow && draft.mode === "both" && draft.uploadStagingKey && (
        <Substep
          id="threads-design"
          label="Pick the design's thread colour"
          complete={confirmed.has("threads-design")}
          summary={threadSummary(draft.logoThreadCodes)}
          openStep={openStep}
          onToggle={toggleStep}
        >
          <ThreadPicker
            threads={config.threads}
            value={draft.logoThreadCodes}
            onChange={(codes) =>
              pickThread(
                "threads-design",
                () => set({ logoThreadCodes: codes }),
                codes.length > 0,
              )
            }
            max={1}
            matchOption={designMatchAllowed}
            matchHintExtra={photoMatchHintExtra}
            matchPriceCents={matchPriceCents}
            heading="Design thread"
          />
          {/* Wave 1 (tap tax): no Confirm row - single-select picker,
              the swatch tap itself confirms (pickThread). */}
        </Substep>
      )}
      {activePlacement && !isSingleDesignFlow && draft.mode === "both" && draft.text.trim() && (draft.fontId || draft.wordArtStyleId) && (
        <Substep
          id="threads-text"
          label="Pick the text's thread colour"
          complete={confirmed.has("threads-text")}
          summary={threadSummary(draft.textThreadCodes)}
          openStep={openStep}
          onToggle={toggleStep}
        >
          <ThreadPicker
            threads={config.threads}
            value={draft.textThreadCodes}
            onChange={(codes) =>
              pickThread(
                "threads-text",
                () => set({ textThreadCodes: codes }),
                codes.length > 0,
              )
            }
            max={1}
            heading="Text thread"
          />
          {/* Wave 1 (tap tax): no Confirm row - single-select picker,
              the swatch tap itself confirms (pickThread). */}
        </Substep>
      )}

      {/* Varsity / college: the arched text is stitched with an outline.
        * This step lets the customer pick the outline's thread colour —
        * it shows in the visualiser and is sent to the embroidery team. */}
      {activePlacement && isVarsity && draft.text.trim() && (
        <Substep
          id="outline"
          label="Choose a text outline colour"
          complete={!!draft.outlineThreadCode}
          summary={
            draft.outlineThreadCode
              ? threadSummary([draft.outlineThreadCode])
              : ""
          }
          openStep={openStep}
          onToggle={toggleStep}
        >
          <ThreadPicker
            threads={config.threads}
            value={draft.outlineThreadCode ? [draft.outlineThreadCode] : []}
            onChange={(codes) =>
              pickThread(
                "outline",
                () => set({ outlineThreadCode: codes[0] ?? null }),
                codes.length > 0,
              )
            }
            max={1}
            heading="Outline thread"
            hint="The college-style text is stitched with an outline. Pick the thread colour for that outline."
          />
          {/* Wave 1 (tap tax): no Confirm row - single-select picker,
              the swatch tap itself confirms (pickThread). */}
        </Substep>
      )}

      {/* Phase 67c: when the template locks size to a single mm value
        * (e.g. pet-varsity = 300mm), hide the slider entirely. The
        * draft already has sizeMm set to the locked value via the
        * useEffect above. A tiny info chip confirms the fixed size so
        * the customer isn't left wondering "where's the size picker?". */}
      {activePlacement && draft.mode && lockedSizeMm && (
        <div className="rk-pc-locked-size">
          <span className="rk-pc-locked-size__label">Embroidery size</span>
          <strong>{lockedSizeMm}mm</strong>
          <span className="rk-pc-locked-size__hint">Included in the garment price.</span>
        </div>
      )}

      {!complete &&
        (() => {
          // isDraftComplete and missingReasons are separate implementations of
          // the same rules, so they can drift. When they do, the customer sees
          // an empty list above a disabled button and has no way to work out
          // what is wrong. Never render the empty state.
          const reasons = missingReasons(draft, config);
          const shown =
            reasons.length > 0
              ? reasons
              : ["Something's missing on this spot. Tap CHANGE on each step above to check it."];
          return (
            <div className="rk-pc-editor-missing">
              <span className="rk-pc-editor-missing__head">Still to do</span>
              <ul className="rk-pc-editor-missing__list">
                {shown.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          );
        })()}
      <div
        className="rk-pc-editor-actions"
        onClick={(e) => {
          // Mobile audit 2026-07-25: a DISABLED Save spot eats the tap with
          // zero feedback (disabled buttons emit no events; the tap lands
          // here on the wrapper). Pulse the "Still to do" list so the dead
          // tap points at what's missing - same affordance the blocked ATC
          // already has (guideBlockedAtc).
          if (complete) return;
          const wrap = (e.currentTarget as HTMLElement).parentElement;
          const missing = wrap?.querySelector<HTMLElement>(".rk-pc-editor-missing");
          if (missing) {
            missing.classList.remove("is-pulsing");
            void missing.offsetWidth;
            missing.classList.add("is-pulsing");
          }
        }}
      >
        <button
          type="button"
          className="rk-pc-cta rk-pc-cta--ghost"
          onClick={onCancel}
        >
          {isEditingExisting ? "Cancel edit" : "Cancel"}
        </button>
        <button
          type="button"
          className="rk-pc-cta"
          disabled={!complete || uploadBusy}
          onClick={onSave}
        >
          {uploadBusy
            ? "Uploading your file…"
            : isEditingExisting
              ? "Update spot"
              : "Save spot"}
        </button>
      </div>
    </div>
  );
}

// ─── Internal: arrangement picker (Above/Below/Left/Right) ──────────────

function ArrangementPicker({
  value,
  onChange,
}: {
  value: Arrangement | null;
  onChange: (next: Arrangement) => void;
}) {
  const options: { id: Arrangement; label: string; diagram: React.ReactNode }[] = [
    { id: "above", label: "Text above design", diagram: <ArrDiagram pos="above" /> },
    { id: "below", label: "Text below design", diagram: <ArrDiagram pos="below" /> },
    { id: "left", label: "Text left of design", diagram: <ArrDiagram pos="left" /> },
    { id: "right", label: "Text right of design", diagram: <ArrDiagram pos="right" /> },
  ];
  return (
    <div className="rk-pc-arr-grid">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          className={`rk-pc-arr-tile${value === o.id ? " is-selected" : ""}`}
          onClick={() => onChange(o.id)}
        >
          {o.diagram}
          <span className="rk-pc-arr-tile__label">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

// Tiny SVG showing text + logo arrangement. Logo is the pink square, text
// is the dark pill.
function ArrDiagram({ pos }: { pos: Arrangement }) {
  // Coordinates in a 60x40 viewBox. The two shapes get repositioned
  // depending on the arrangement.
  const layouts = {
    above: { txt: { x: 18, y: 6, w: 24, h: 6 }, logo: { x: 22, y: 16, w: 16, h: 16 } },
    below: { txt: { x: 18, y: 28, w: 24, h: 6 }, logo: { x: 22, y: 8, w: 16, h: 16 } },
    left: { txt: { x: 4, y: 14, w: 16, h: 8 }, logo: { x: 30, y: 11, w: 16, h: 16 } },
    right: { txt: { x: 40, y: 14, w: 16, h: 8 }, logo: { x: 14, y: 11, w: 16, h: 16 } },
  } as const;
  const l = layouts[pos];
  return (
    <svg viewBox="0 0 60 40" className="rk-pc-arr-tile__diagram" aria-hidden>
      <rect x={l.logo.x} y={l.logo.y} width={l.logo.w} height={l.logo.h} rx="2" fill="#f38bb9" />
      <rect x={l.txt.x} y={l.txt.y} width={l.txt.w} height={l.txt.h} rx="3" fill="#1f2937" />
    </svg>
  );
}

// ─── Internal: editor sub-step heading ────────────────────────────────────

// Confirm button shown at the foot of sub-steps that have no natural
// "done" event (size slider, thread colour pick). Clicking it marks the
// step confirmed so the accordion flows on to the next step.
function StepConfirm({
  onConfirm,
  disabled,
  label,
}: {
  onConfirm: () => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <div className="rk-pc-substep-confirm-row">
      <button
        type="button"
        className="rk-pc-substep-confirm"
        onClick={onConfirm}
        disabled={disabled}
      >
        {label}
      </button>
    </div>
  );
}

function Substep({
  id,
  label,
  complete,
  summary,
  openStep,
  onToggle,
  children,
}: {
  id: string;
  label: string;
  complete: boolean;
  summary: string;
  openStep: string | null;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}) {
  const open = openStep === id;
  return (
    <div
      data-substep={id}
      className={`rk-pc-substep${open ? " is-open" : ""}${complete ? " is-complete" : ""}`}
    >
      <button
        type="button"
        className="rk-pc-substep__header"
        onClick={() => onToggle(id)}
        aria-expanded={open}
      >
        <span className="rk-pc-substep__marker" aria-hidden>
          {complete ? "✓" : ""}
        </span>
        <span className="rk-pc-substep__heading">
          <span className="rk-pc-substep__label">{label}</span>
          {!open && summary ? (
            <span className="rk-pc-substep__summary">{summary}</span>
          ) : null}
        </span>
        <span
          className={`rk-pc-substep__chevron${!open && complete ? " rk-pc-substep__chevron--change" : ""}`}
          aria-hidden
        >
          {/* A done + collapsed step reads "Change" instead of a bare
              chevron - with auto-advance collapsing steps the moment
              they're picked, the way back has to be obvious. */}
          {open ? "▾" : complete ? "Change" : "▸"}
        </span>
      </button>
      <div className="rk-pc-collapse" data-open={open}>
        <div className="rk-pc-collapse__clip">
          <div className="rk-pc-substep__body">{children}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Internal: extra-upload adder (pet/person multi-image) ──────────────
//
// A slim "+ Add another <subject> photo" tile. Distinct from LogoDropZone
// because (a) it has no current-file preview to render, (b) the copy is
// subject-specific (pet vs person - 2026-07-19), (c) on success it
// returns the upload to a parent list instead of binding to a single
// draft field.

function ExtraUploadAdder({
  workerUrl,
  customerEmail,
  acceptedMimeTypes,
  fileMaxMb,
  subject,
  onUploaded,
}: {
  workerUrl: string;
  customerEmail: string;
  acceptedMimeTypes: string[];
  fileMaxMb: number;
  /** Noun for the button copy: "pet" (default) or "person". */
  subject: string;
  onUploaded: (
    res: {
      stagingKey: string;
      contentType: string;
      size: number;
      fileHash?: string;
    },
    file: File,
  ) => void;
}) {
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputId = React.useId();

  const handle = async (file: File) => {
    setError(null);
    if (file.size > fileMaxMb * 1024 * 1024) {
      setError(`File over ${fileMaxMb}MB; try a smaller one.`);
      return;
    }
    setUploading(true);
    try {
      const res = await uploadStaging(workerUrl, file, customerEmail);
      onUploaded(res, file);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rk-pc-extra-upload-add">
      <input
        id={inputId}
        type="file"
        accept={acceptedMimeTypes.join(",")}
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handle(f);
          e.target.value = "";
        }}
      />
      <label htmlFor={inputId} className="rk-pc-extra-upload-add__btn">
        {uploading ? "Uploading…" : `+ Add another ${subject} photo`}
      </label>
      {error && <span className="rk-pc-extra-upload-add__error">{error}</span>}
    </div>
  );
}

// ─── Internal: design drop zone ───────────────────────────────────────────

function LogoDropZone({
  workerUrl,
  stagingKey,
  filename,
  mimeType,
  acceptedMimeTypes,
  fileMaxMb,
  onUploaded,
  customerEmail,
  onBusyChange,
}: {
  workerUrl: string;
  stagingKey: string | null;
  filename: string | null;
  mimeType: string | null;
  acceptedMimeTypes: string[];
  fileMaxMb: number;
  onUploaded: (
    res: {
      stagingKey: string;
      contentType: string;
      size: number;
      fileHash?: string;
      alreadyDigitised?: boolean;
      firstOrderName?: string | null;
    },
    file: File,
    /** Wave 1 funnel: wall-clock ms the staging upload took. */
    uploadMs: number,
  ) => void | Promise<void>;
  customerEmail: string;
  /** RK26682 hardening: reports whether an upload is in flight. */
  onBusyChange?: (busy: boolean) => void;
}) {
  const [uploading, setUploading] = React.useState(false);
  const onBusyChangeRef = React.useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;
  React.useEffect(() => {
    onBusyChangeRef.current?.(uploading);
  }, [uploading]);
  React.useEffect(() => () => onBusyChangeRef.current?.(false), []);
  const [error, setError] = React.useState<string | null>(null);
  const [over, setOver] = React.useState(false);
  // Wave 1 (dead-click killer): a re-tap while an upload is in flight
  // flashes a "still uploading" note instead of silently doing nothing.
  const [uploadWait, setUploadWait] = React.useState(false);
  const uploadWaitTimerRef = React.useRef<number | null>(null);
  // Phase 40: instant "press" feedback. The OS file picker takes 200-400ms
  // to appear, during which the dropzone DOM doesn't change — Clarity
  // (and customers) reads that as a dead click. Toggling `opening` on
  // pointerdown gives an immediate visual cue and stops re-click spam.
  const [opening, setOpening] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const send = async (file: File) => {
    setError(null);
    // 2026-07-02: client-side pre-checks so a phone photo never uploads
    // fully on mobile data just to be rejected, and errors read like a
    // human wrote them (the raw "upload_failed: 400 ..." strings were
    // rendered verbatim to customers).
    // RK24356: an oversized photo used to be refused with "make it smaller
    // yourself", which is how a real customer ended up abandoning her
    // upload and putting her order on hold. Resize it for her instead, and
    // only complain when it genuinely cannot be re-encoded (e.g. a huge
    // PDF).
    const sizeMb = file.size / (1024 * 1024);
    let outgoing = file;
    if (sizeMb > fileMaxMb) {
      if (!isShrinkableImage(file)) {
        setError(
          `That file is ${sizeMb.toFixed(1)}MB and our max is ${fileMaxMb}MB. ` +
            `A JPG or PNG photo of it will upload fine.`,
        );
        return;
      }
      setUploading(true);
      try {
        outgoing = await shrinkImage(file, fileMaxMb * 1024 * 1024);
      } catch {
        setUploading(false);
        setError(
          `That file is ${sizeMb.toFixed(1)}MB and we couldn't resize it. ` +
            `Try a screenshot of it instead.`,
        );
        return;
      }
    }
    const name = (file.name || "").toLowerCase();
    if (file.type === "image/heic" || file.type === "image/heif" || /\.heic$|\.heif$/.test(name)) {
      setError(
        "That's an iPhone HEIC photo, which we can't read yet. Open it in " +
          "Photos and share it as a JPEG, or screenshot it and upload the " +
          "screenshot.",
      );
      return;
    }
    setUploading(true);
    const startedAt = Date.now();
    try {
      const res = await uploadStaging(workerUrl, outgoing, customerEmail);
      // Awaited (RK26682): the handler classifies the photo before it
      // writes the draft, so "uploading" must stay true until the new file
      // is really in the draft, not just on the server.
      await onUploaded(res, outgoing, Date.now() - startedAt);
    } catch (e) {
      const raw = (e as Error).message || "";
      // Map machine errors to human copy; keep the raw code out of the UI.
      let friendly = "That upload didn't go through. Give it another try.";
      if (/invalid_mime/.test(raw)) {
        friendly = "We can't read that file type. PNG, JPG, WEBP, SVG or PDF works best.";
      } else if (/unsafe_svg/.test(raw)) {
        // util/svgSafe refused it: scripts, links or embedded HTML inside
        friendly = "That SVG has scripts or links inside it, which we can't accept. Export it again as a plain SVG, or as a PNG.";
      } else if (/file_too_large|413/.test(raw)) {
        friendly = `That file is over our ${fileMaxMb}MB limit. A smaller export or a screenshot of it will work.`;
      } else if (/rate|429/.test(raw)) {
        friendly = "Too many uploads in a row. Give it a few seconds and try again.";
      }
      setError(friendly);
    } finally {
      setUploading(false);
      setUploadWait(false);
    }
  };

  const openPicker = () => {
    if (uploading) {
      // Wave 1 (dead-click killer): the OS picker stays shut while an
      // upload is in flight, but the tap must visibly respond - flash
      // the "still uploading" note (re-tapping restarts the timer).
      setUploadWait(true);
      if (uploadWaitTimerRef.current) {
        window.clearTimeout(uploadWaitTimerRef.current);
      }
      uploadWaitTimerRef.current = window.setTimeout(
        () => setUploadWait(false),
        1600,
      );
      return;
    }
    setOpening(true);
    // Drop the "opening" state on the next paint after the picker has had
    // a chance to render. Window blur is the most reliable signal that
    // the picker actually opened; we also clear after 1.2s as a fallback
    // for the cancel-without-blur case.
    const clear = () => setOpening(false);
    const t = window.setTimeout(clear, 1200);
    const onBlur = () => {
      window.clearTimeout(t);
      window.removeEventListener("blur", onBlur);
      // Slight delay so the pressed style is visible for at least a frame.
      window.setTimeout(clear, 60);
    };
    window.addEventListener("blur", onBlur, { once: true });
    inputRef.current?.click();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) send(f);
  };

  const isImage = mimeType && /image\//.test(mimeType) && mimeType !== "application/pdf";
  const label = uploading
    ? "Uploading…"
    : opening
      ? "Opening…"
      : stagingKey
        ? "Replace file"
        : "Drop your design here";
  return (
    <div
      className={`rk-pc-drop${over ? " is-over" : ""}${opening ? " is-opening" : ""}${uploading ? " is-uploading" : ""}`}
      role="button"
      tabIndex={0}
      aria-busy={uploading || opening}
      aria-label={label}
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPicker();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
    >
      {stagingKey && isImage ? (
        <img
          src={uploadedFileUrl(workerUrl, stagingKey)}
          alt={filename ?? "uploaded design"}
          style={{ maxHeight: 80, maxWidth: "100%", objectFit: "contain", pointerEvents: "none" }}
        />
      ) : (
        <span
          style={{ font: "32px/1 var(--rk-pc-font-display)", color: "var(--rk-pc-pink)", pointerEvents: "none" }}
        >
          ↑
        </span>
      )}
      <span className="rk-pc-drop__title">{label}</span>
      {/* Wave 1: in-flight feedback. The staging upload runs over fetch
          (no progress events), so the bar is indeterminate - do not
          rewrite the transport for a percent readout. */}
      {uploading && (
        <span className="rk-pc-drop__progress" aria-hidden>
          <span className="rk-pc-drop__progress-bar" />
        </span>
      )}
      {uploading && uploadWait && (
        <span className="rk-pc-drop__wait" role="status">
          Hang on - still uploading
        </span>
      )}
      <span className="rk-pc-drop__sub">
        {/* human names, not MIME strings ("image/png · application/pdf") */}
        {acceptedMimeTypes
          .map((m) => (m.split("/")[1] || m).replace("jpeg", "jpg").replace("svg+xml", "svg").toUpperCase())
          .join(", ")}{" "}
        · {fileMaxMb}MB max
      </span>
      {filename && <span className="rk-pc-drop__filename">{filename}</span>}
      <input
        ref={inputRef}
        type="file"
        accept={acceptedMimeTypes.join(",")}
        // Hidden but interactive: `display:none` breaks the file picker
        // on some mobile browsers, so push it off-screen instead.
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          border: 0,
        }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) send(f);
          e.target.value = "";
        }}
      />
      {error && (
        <span
          role="alert"
          style={{
            color: "#9f1239",
            fontSize: stagingKey ? 14 : 12,
            fontWeight: stagingKey ? 700 : 400,
          }}
        >
          {/* RK26682 hardening: a failed REPLACE used to leave the old
              thumbnail up with a 12px note, which reads as success. */}
          {stagingKey
            ? `Your new file did not upload, so this spot still uses ${filename ?? "your previous file"}. `
            : ""}
          {error}
        </span>
      )}
    </div>
  );
}
